import React, { useState, useEffect, useCallback } from "react";
import {
  getDatabase,
  ref,
  set,
  get,
  push,
  remove,
  update,
} from "firebase/database";
import {
  getAuth,
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  GoogleAuthProvider,
  updatePassword,
} from "firebase/auth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FanpageTokenManager from "./components/FanpageTokenManager";
import {
  exchangeForLongLivedToken,
  validateToken,
} from "./services/tokenService";
import {
  FACEBOOK_COMMENT_REQUIRED_SCOPES,
  FACEBOOK_MANAGEMENT_SCOPE,
  fetchGrantedFacebookScopes,
  buildConnectedFanpageRecord,
} from "./utils/facebookCommentPermissions";
import axios from "axios";
import "./ProfileScreen.css";
import {
  generateDebugReport,
  getFacebookErrorMessage,
} from "./utils/facebookDebug";
import { createFacebookLoginFallback } from "./utils/facebookLoginFallback";
import { normalizeWordPressSiteUrl } from "./utils/wordpressUrl";
import SettingTooltip, {
  WEBSITE_CONTACT_INFO_FIELD_TOOLTIP_TEXT,
  WEBSITE_INFO_TOOLTIP_TEXT,
} from "./components/SettingTooltip";
import { app } from "./firebase/config";
import {
  disconnectZaloAccount,
  startZaloOAuth,
} from "./services/zaloService";
import { buildFacebookConnectedChannelMap } from "./utils/socialChannels";
import { useLocation } from "react-router-dom";
import { checkSeoConnector, getConnectorDownloadUrl } from "./api/wordpressConnector";

const ProfileScreen = () => {
  const location = useLocation();
  const [userId, setUserId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [fanpages, setFanpages] = useState([]);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [referralPhone, setReferralPhone] = useState("");
  const [isTrial, setIsTrial] = useState(false);
  const [selectedFanpage, setSelectedFanpage] = useState(null);
  const [pointsDetail, setPointsDetail] = useState({
    points: 0,
    purchasedPoints: 0,
    lastUpdated: null,
  });
  const [membershipInfo, setMembershipInfo] = useState({
    currentMembership: null,
    expiryDate: null,
    isYearly: false,
  });
  const [authProviders, setAuthProviders] = useState([]);
  const [hasPassword, setHasPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [changePasswordValue, setChangePasswordValue] = useState("");
  const [confirmChangePassword, setConfirmChangePassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Hàm helper để loại bỏ các giá trị undefined trước khi update Firebase
  const sanitizeForFirebase = (obj) => {
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        if (value === null || typeof value !== "object") {
          sanitized[key] = value;
        } else if (Array.isArray(value)) {
          sanitized[key] = value.filter((item) => item !== undefined);
        } else {
          sanitized[key] = sanitizeForFirebase(value);
        }
      }
    }
    return sanitized;
  };

  const [wordpressSites, setWordpressSites] = useState([]);
  const [isEditingSite, setIsEditingSite] = useState(false);
  const [currentSite, setCurrentSite] = useState({
    id: null,
    url: "",
    username: "",
    password: "",
    siteName: "",
    websiteInfo: "",
    contactInfo: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // States cho SEO Connector
  const [showConnectorDialog, setShowConnectorDialog] = useState(false);
  const [checkingConnector, setCheckingConnector] = useState(false);
  const [selectedSiteForConnector, setSelectedSiteForConnector] = useState(null);
  const [showDetailedGuide, setShowDetailedGuide] = useState(false);

  const [availableFanpages, setAvailableFanpages] = useState([]);
  const [needsFacebookConnect, setNeedsFacebookConnect] = useState(false);
  const [connectedFanpages, setConnectedFanpages] = useState([]);
  const [zaloAccounts, setZaloAccounts] = useState([]);
  const [isLoadingZaloAccounts, setIsLoadingZaloAccounts] = useState(false);
  const [isConnectingZalo, setIsConnectingZalo] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = location.state?.activeTab || params.get("tab");
    if (
      ["overview", "facebook", "zalo", "wordpress", "security"].includes(
        requestedTab
      )
    ) {
      setActiveTab(requestedTab);
    }
  }, [location.search, location.state]);
  const [isLoadingFanpages, setIsLoadingFanpages] = useState(false);
  const [isRefreshingFanpages, setIsRefreshingFanpages] = useState(false);
  const [isAddingFanpage, setIsAddingFanpage] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const loadZaloAccounts = useCallback(async (targetUserId = userId) => {
    if (!targetUserId) return;

    setIsLoadingZaloAccounts(true);
    try {
      const db = getDatabase();
      const snapshot = await get(
        ref(db, `users/${targetUserId}/connectedChannels/zalo`)
      );
      setZaloAccounts(snapshot.exists() ? Object.values(snapshot.val()) : []);
    } catch (error) {
      console.error("Không thể tải danh sách Zalo OA:", error);
      toast.error("Không thể tải danh sách Zalo OA", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setIsLoadingZaloAccounts(false);
    }
  }, [userId]);

  const handleConnectZalo = async () => {
    const targetUserId = userId || localStorage.getItem("userId");
    if (!targetUserId) {
      toast.error("Vui lòng đăng nhập lại trước khi kết nối Zalo", {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }

    setIsConnectingZalo(true);
    try {
      const result = await startZaloOAuth({
        userId: targetUserId,
        returnUrl: `${window.location.origin}${window.location.pathname}`,
      });

      if (!result?.authUrl) {
        throw new Error("Backend không trả về URL kết nối Zalo");
      }

      window.location.href = result.authUrl;
    } catch (error) {
      console.error("Không thể bắt đầu kết nối Zalo:", error);
      toast.error(error.message || "Không thể kết nối Zalo OA", {
        position: "top-right",
        autoClose: 5000,
      });
      setIsConnectingZalo(false);
    }
  };

  const handleDisconnectZalo = async (accountId) => {
    const targetUserId = userId || localStorage.getItem("userId");
    if (!targetUserId || !accountId) return;

    try {
      await disconnectZaloAccount({
        userId: targetUserId,
        accountId,
      });
      setZaloAccounts((current) =>
        current.filter((account) => account.accountId !== accountId)
      );
      toast.success("Đã ngắt kết nối Zalo OA", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Không thể ngắt kết nối Zalo:", error);
      toast.error(error.message || "Không thể ngắt kết nối Zalo OA", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  // Debug useEffect to track state changes
  useEffect(() => {
    console.log("🔍 ProfileScreen State Debug:", {
      connectedFanpagesLength: connectedFanpages.length,
      isLoadingFanpages,
      isRefreshingFanpages,
      needsFacebookConnect,
      shouldShowEmptyState:
        connectedFanpages.length === 0 &&
        !isLoadingFanpages &&
        !isRefreshingFanpages &&
        !needsFacebookConnect,
    });
  }, [
    connectedFanpages.length,
    isLoadingFanpages,
    isRefreshingFanpages,
    needsFacebookConnect,
  ]);

  // Hàm kiểm tra kết nối WordPress
  const testWordPressConnection = async (siteData) => {
    try {
      if (!siteData.url || !siteData.username || !siteData.password) {
        throw new Error("Vui lòng nhập đầy đủ thông tin website");
      }

      // Kiểm tra định dạng URL
      try {
        new URL(siteData.url);
      } catch (e) {
        throw new Error("URL không hợp lệ. Vui lòng kiểm tra lại!");
      }

      // Chuẩn hóa URL
      const baseUrl = normalizeWordPressSiteUrl(siteData.url);

      // URL API WordPress REST
      const apiUrl = `${baseUrl}/wp-json/wp/v2/users/me`;

      console.log("🔄 Đang kiểm tra kết nối WordPress...", {
        url: apiUrl,
        username: siteData.username,
      });

      // Gọi API với Basic Authentication
      const response = await axios.get(apiUrl, {
        auth: {
          username: siteData.username,
          password: siteData.password,
        },
        timeout: 15000, // Timeout 15 giây
      });

      console.log("✅ Kết nối WordPress thành công:", {
        userId: response.data.id,
        userName: response.data.name,
        userEmail: response.data.email,
        roles: response.data.roles,
      });

      return {
        success: true,
        user: response.data,
        message: `Kết nối thành công! Đăng nhập với tài khoản: ${response.data.name}`,
      };
    } catch (error) {
      console.error("❌ Lỗi khi kiểm tra kết nối WordPress:", error);

      let errorMessage = "Không thể kết nối đến WordPress";

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        switch (status) {
          case 401:
            if (errorData?.code === "invalid_username") {
              errorMessage =
                "Tên người dùng không tồn tại. Hãy kiểm tra lại Username (Tên người dùng), không phải Tên hiển thị hay Tên mật khẩu ứng dụng";
            } else if (errorData?.code === "incorrect_password") {
              errorMessage =
                "Application Password không chính xác. Hãy kiểm tra lại mật khẩu ứng dụng, không phải mật khẩu đăng nhập thông thường";
            } else {
              errorMessage =
                "Lỗi xác thực. Vui lòng kiểm tra Username (Tên người dùng) và Application Password";
            }
            break;
          case 403:
            errorMessage =
              "Không có quyền truy cập. Vui lòng kiểm tra quyền người dùng";
            break;
          case 404:
            errorMessage =
              "Không tìm thấy WordPress REST API. Vui lòng kiểm tra URL và đảm bảo WordPress đã kích hoạt REST API";
            break;
          case 500:
            errorMessage =
              "Lỗi máy chủ WordPress. Vui lòng kiểm tra lại cấu hình server";
            break;
          default:
            errorMessage = `Lỗi HTTP ${status}: ${
              errorData?.message || error.message
            }`;
        }
      } else if (error.code === "ENOTFOUND" || error.code === "ECONNREFUSED") {
        errorMessage =
          "Không thể kết nối đến website. Vui lòng kiểm tra URL và kết nối internet";
      } else if (error.code === "ECONNABORTED") {
        errorMessage =
          "Kết nối bị timeout. Website phản hồi quá chậm hoặc không khả dụng";
      } else {
        errorMessage = error.message || "Lỗi không xác định";
      }

      return {
        success: false,
        message: errorMessage,
        error: error.response?.data || error.message,
      };
    }
  };

  // Hàm kiểm tra cấu hình Facebook App
  const checkFacebookAppConfig = () => {
    const requiredEnvVars = {
      REACT_APP_FB_CLIENT_ID: process.env.REACT_APP_FB_CLIENT_ID,
      REACT_APP_FB_CLIENT_SECRET: process.env.REACT_APP_FB_CLIENT_SECRET,
    };

    const missingVars = Object.entries(requiredEnvVars)
      .filter(([key, value]) => !value)
      .map(([key]) => key);

    if (missingVars.length > 0) {
      console.error("❌ Missing Facebook environment variables:", missingVars);
      toast.error(
        "Cấu hình Facebook App không đầy đủ. Vui lòng liên hệ admin.",
        {
          position: "top-right",
          autoClose: 7000,
        }
      );
      return false;
    }

    console.log("✅ Facebook App configuration is valid");
    return true;
  };

  const initFacebookSDK = useCallback(() => {
    const SDK_TIMEOUT_MS = 10000; // 10 giây timeout
    return new Promise((resolve, reject) => {
      if (window.FB) {
        resolve();
        return;
      }

      // Timeout để tránh Promise treo mãi khi FB SDK fail load
      const timeoutId = setTimeout(() => {
        console.error("⏰ [FB SDK] Timeout after 10s — SDK failed to initialize");
        reject(new Error("Facebook SDK initialization timed out"));
      }, SDK_TIMEOUT_MS);

      if (document.getElementById("facebook-jssdk")) {
        const checkFBInterval = setInterval(() => {
          if (window.FB) {
            clearInterval(checkFBInterval);
            clearTimeout(timeoutId);
            resolve();
          }
        }, 100);
      } else {
        window.fbAsyncInit = function () {
          window.FB.init({
            appId: process.env.REACT_APP_FB_CLIENT_ID,
            cookie: true,
            xfbml: true,
            version: "v21.0",
          });
          clearTimeout(timeoutId);
          resolve();
        };

        (function (d, s, id) {
          var js,
            fjs = d.getElementsByTagName(s)[0];
          if (d.getElementById(id)) return;
          js = d.createElement(s);
          js.id = id;
          js.src = "https://connect.facebook.net/en_US/sdk.js";
          fjs.parentNode.insertBefore(js, fjs);
        })(document, "script", "facebook-jssdk");
      }
    });
  }, []);

  // Utility: redirect trực tiếp đến Facebook OAuth dialog (fallback khi FB.login popup fail)
  const startFBRedirectLogin = (pendingAction = "connect_fanpage") => {
    const redirectAfter = window.location.pathname || "/profile";

    // Lưu pending action và trang hiện tại để xử lý khi redirect về
    localStorage.setItem("fb_pending_action", pendingAction);
    localStorage.setItem("fb_redirect_after", redirectAfter);
    const appId = process.env.REACT_APP_FB_CLIENT_ID;
    const redirectUri = encodeURIComponent(
      window.location.origin + redirectAfter
    );
    const scope = FACEBOOK_MANAGEMENT_SCOPE;
    const fbLoginUrl = `https://www.facebook.com/v21.0/dialog/oauth?client_id=${appId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=token&auth_type=rerequest`;
    console.log(
      "🟢 [FB Redirect] Redirecting to Facebook OAuth, will return to:",
      redirectAfter
    );
    window.location.href = fbLoginUrl;
  };

  // Utility: gọi FB.login với timeout fallback sang redirect
  const fbLoginWithTimeout = (
    fbLoginOptions,
    onSuccess,
    onFail,
    {
      timeoutMs = 3000,
      fallbackPendingAction = "connect_fanpage",
    } = {}
  ) => {
    const fallbackController = createFacebookLoginFallback({
      timeoutMs,
      onFallback: () => {
        console.warn(
          "⚠️ [FB Login] FB.login() timeout trước khi popup được mở, fallback sang redirect"
        );
        startFBRedirectLogin(fallbackPendingAction);
      },
    });

    fallbackController.start();

    try {
      window.FB.login(
        (response) => {
          fallbackController.markCallbackHandled();
          if (response.status === "connected") {
            onSuccess(response);
          } else {
            onFail(response);
          }
        },
        fbLoginOptions
      );
    } catch (error) {
      fallbackController.cleanup();
      console.error("❌ [FB Login] FB.login() threw error:", error);
      startFBRedirectLogin(fallbackPendingAction);
    }
  };

  // Hàm kiểm tra và làm mới Facebook token nếu hết hạn hoặc lỗi
  const checkAndRefreshUserToken = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return false;

      // Kiểm tra xem người dùng đăng nhập bằng phương thức nào
      const loginMethod = localStorage.getItem("loginMethod") || "unknown";
      console.log(`Phương thức đăng nhập: ${loginMethod}`);

      // Lấy Facebook token từ localStorage (nếu có)
      let facebookAccessToken = localStorage.getItem("facebookAccessToken");

      // Nếu không có facebookAccessToken riêng, kiểm tra xem userAccessToken có phải là token Facebook không
      if (!facebookAccessToken) {
        const userAccessToken = localStorage.getItem("userAccessToken");
        if (!userAccessToken) {
          console.log("Không có token Facebook trong localStorage");
          return false;
        }

        // Kiểm tra xem userAccessToken có phải là token Facebook không bằng cách kiểm tra JWT structure
        // Facebook access token sẽ không có dấu "." (JWT format)
        if (userAccessToken.includes(".")) {
          console.log(
            "userAccessToken là Firebase JWT token, không phải Facebook token"
          );
          return false;
        }

        // Kiểm tra xem userAccessToken có phải là token Facebook không
        try {
          const tokenStatus = await validateToken(userAccessToken);
          if (tokenStatus.isValid) {
            // Nếu là token Facebook hợp lệ, lưu vào facebookAccessToken
            localStorage.setItem("facebookAccessToken", userAccessToken);
            facebookAccessToken = userAccessToken;
            console.log("Đã sao chép userAccessToken sang facebookAccessToken");
          } else {
            console.log("userAccessToken không phải là token Facebook hợp lệ");
            return false;
          }
        } catch (error) {
          console.log(
            "userAccessToken không phải là token Facebook hợp lệ:",
            error.message
          );
          return false;
        }
      }

      // Kiểm tra token trong database
      const db = getDatabase();
      const facebookAuthRef = ref(db, `users/${userId}/facebookAuth`);
      const facebookAuthSnapshot = await get(facebookAuthRef);

      // Nếu không có thông tin Facebook trong database và không có token Facebook trong localStorage
      if (!facebookAuthSnapshot.exists() && !facebookAccessToken) {
        console.log("Người dùng chưa kết nối với Facebook");
        setNeedsFacebookConnect(true);
        return false;
      }

      // Lấy token từ database nếu có
      const facebookAuth = facebookAuthSnapshot.exists()
        ? facebookAuthSnapshot.val()
        : null;
      const dbFacebookToken = facebookAuth?.userAccessToken;

      // Ưu tiên sử dụng token từ localStorage, nếu không có thì dùng token từ database
      const tokenToCheck = facebookAccessToken || dbFacebookToken;

      if (!tokenToCheck) {
        console.log("Không có Facebook token để kiểm tra");
        return false;
      }

      // Kiểm tra tính hợp lệ của token Facebook
      console.log("Đang kiểm tra tính hợp lệ của Facebook token...");
      const tokenStatus = await validateToken(tokenToCheck);

      // Nếu token hợp lệ và chưa gần hết hạn, không cần làm gì
      if (tokenStatus.isValid) {
        // Kiểm tra xem token có sắp hết hạn không (còn dưới 7 ngày)
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        const expiryTime = tokenStatus.expiresAt;

        if (expiryTime && expiryTime - Date.now() > sevenDaysInMs) {
          console.log("Facebook token còn hợp lệ và chưa gần hết hạn");

          // Đảm bảo token được lưu ở cả localStorage và database
          if (!facebookAccessToken && dbFacebookToken) {
            localStorage.setItem("facebookAccessToken", dbFacebookToken);
            console.log(
              "Đã cập nhật facebookAccessToken từ database vào localStorage"
            );
          } else if (
            facebookAccessToken &&
            (!dbFacebookToken || facebookAccessToken !== dbFacebookToken)
          ) {
            // Cập nhật token vào database nếu khác nhau
            await update(facebookAuthRef, {
              userAccessToken: facebookAccessToken,
              lastUpdated: Date.now(),
            });
            console.log(
              "Đã cập nhật facebookAccessToken từ localStorage vào database"
            );
          }

          return true;
        }
      }

      console.log(
        "Facebook token không hợp lệ hoặc sắp hết hạn, đang làm mới..."
      );

      // Nếu token không hợp lệ hoặc sắp hết hạn, yêu cầu token mới
      await initFacebookSDK();

      // Thử sử dụng token từ database nếu khác với token trong localStorage
      if (
        dbFacebookToken &&
        (!facebookAccessToken || dbFacebookToken !== facebookAccessToken)
      ) {
        console.log("Thử sử dụng token từ database...");
        const dbTokenStatus = await validateToken(dbFacebookToken);

        if (dbTokenStatus.isValid) {
          // Nếu token trong database hợp lệ, sử dụng nó
          localStorage.setItem("facebookAccessToken", dbFacebookToken);

          if (facebookAuth.userAccessTokenExpiry) {
            localStorage.setItem(
              "facebookAccessTokenExpiry",
              facebookAuth.userAccessTokenExpiry
            );
          }

          // Nếu người dùng đăng nhập bằng Facebook, cập nhật cả userAccessToken
          if (loginMethod === "facebook") {
            localStorage.setItem("userAccessToken", dbFacebookToken);
            if (facebookAuth.userAccessTokenExpiry) {
              localStorage.setItem(
                "userAccessTokenExpiry",
                facebookAuth.userAccessTokenExpiry
              );
            }
          }

          console.log("Đã cập nhật Facebook token từ database");
          return true;
        }
      }

      // Nếu không thể sử dụng token từ database, yêu cầu người dùng đăng nhập lại
      console.log("Cần yêu cầu token mới từ Facebook...");

      // Hiển thị thông báo cho người dùng
      toast.info("Đang làm mới kết nối với Facebook...", {
        position: "top-right",
        autoClose: 3000,
      });

      // Yêu cầu token mới từ Facebook (sử dụng timeout fallback)
      if (!window.FB || !window.fbInitialized) {
        console.warn("⚠️ FB SDK chưa init, redirect flow");
        startFBRedirectLogin("refresh_token");
        return false;
      }

      return new Promise((resolve) => {
        fbLoginWithTimeout(
          {
            scope: FACEBOOK_MANAGEMENT_SCOPE,
            auth_type: "rerequest",
          },
          // onSuccess
          (response) => {
            const shortLivedToken = response.authResponse.accessToken;
            console.log("🔄 Exchanging short-lived token for long-lived token...");
            fetch(
              `https://graph.facebook.com/v21.0/oauth/access_token?grant_type=fb_exchange_token&client_id=${process.env.REACT_APP_FB_CLIENT_ID}&client_secret=${process.env.REACT_APP_FB_CLIENT_SECRET}&fb_exchange_token=${shortLivedToken}`
            )
              .then((res) => res.json())
              .then((data) => {
                if (data.access_token) {
                  const accessToken = data.access_token;
                  const expiryTime = Date.now() + data.expires_in * 1000;
                  localStorage.setItem("facebookAccessToken", accessToken);
                  localStorage.setItem("facebookAccessTokenExpiry", expiryTime);
                  if (loginMethod === "unknown") localStorage.setItem("loginMethod", "facebook");
                  if (loginMethod === "facebook") {
                    localStorage.setItem("userAccessToken", accessToken);
                    localStorage.setItem("userAccessTokenExpiry", expiryTime);
                  }
                  const db = getDatabase();
                  const userTokenRef = ref(db, `users/${userId}/facebookAuth`);
                  const validExpiryTime = !isNaN(expiryTime) ? expiryTime : Date.now() + 60 * 24 * 60 * 60 * 1000;
                  update(userTokenRef, {
                    userAccessToken: accessToken,
                    userAccessTokenExpiry: validExpiryTime,
                    tokenType: "long_lived_user_token",
                    lastUpdated: Date.now(),
                  })
                    .then(() => {
                      console.log("✅ Đã làm mới Facebook token thành công");
                      toast.success("Đã làm mới kết nối Facebook thành công!", { position: "top-right", autoClose: 3000 });
                      resolve(true);
                    })
                    .catch((tokenError) => {
                      console.error("❌ Lỗi khi lưu token vào Firebase:", tokenError);
                      toast.error("Lỗi khi lưu token: " + tokenError.message, { position: "top-right", autoClose: 5000 });
                      resolve(false);
                    });
                } else {
                  toast.error("Không thể lấy access token từ Facebook", { position: "top-right", autoClose: 5000 });
                  resolve(false);
                }
              })
              .catch((error) => {
                toast.error("Lỗi khi trao đổi token: " + error.message, { position: "top-right", autoClose: 5000 });
                resolve(false);
              });
          },
          // onFail
          (response) => {
            toast.error("Kết nối Facebook không thành công!", { position: "top-right", autoClose: 5000 });
            resolve(false);
          },
          {
            fallbackPendingAction: "refresh_token",
          }
        );
      });
    } catch (error) {
      console.error("Lỗi khi kiểm tra và làm mới Facebook token:", error);
      toast.error(
        "Đã xảy ra lỗi khi kiểm tra kết nối Facebook: " + error.message,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return false;
    }
  };

  // Hàm kiểm tra xem có cần làm mới dữ liệu fanpage hay không
  const shouldRefreshFanpages = (existingFanpages) => {
    // Nếu không có fanpage nào, cần làm mới
    if (!existingFanpages || existingFanpages.length === 0) {
      return true;
    }

    // Kiểm tra nhanh bằng localStorage timestamp (tránh duyệt qua từng fanpage)
    const oneDayInMs = 24 * 60 * 60 * 1000;
    const lastRefreshTime = parseInt(localStorage.getItem("lastFanpageRefreshTime") || "0", 10);
    const timeSinceLastRefresh = Date.now() - lastRefreshTime;

    if (lastRefreshTime > 0 && timeSinceLastRefresh < oneDayInMs) {
      console.log(
        `⏳ Fanpage đã refresh ${Math.round(timeSinceLastRefresh / (60 * 1000))} phút trước, chưa đủ 1 ngày → skip refresh`
      );
      return false;
    }

    // Kiểm tra xem có fanpage nào có token hết hạn không
    const hasExpiredToken = existingFanpages.some((page) => {
      if (!page.tokenExpiry) return false;
      return page.tokenExpiry < Date.now();
    });

    if (hasExpiredToken) {
      console.log("🔄 Có fanpage token hết hạn → cần refresh");
      return true;
    }

    // Đã qua 1 ngày (hoặc chưa có timestamp) → cần refresh
    console.log("🔄 Đã >= 1 ngày kể từ lần refresh cuối → cần refresh");
    return true;
  };

  // Hàm làm mới dữ liệu fanpage từ Facebook
  const handleRefreshFanpages = async () => {
    setIsRefreshingFanpages(true);
    try {
      // Xóa cache timestamp để force refresh
      localStorage.removeItem("lastFanpageRefreshTime");

      await fetchFanpagesFromFacebook();
      toast.success("Đã làm mới dữ liệu fanpage thành công!", {
        position: "top-right",
        autoClose: 3000,
      });

      // Tăng giá trị refreshTrigger để kích hoạt cập nhật trạng thái fanpage trong FanpageTokenManager
      setRefreshTrigger((prev) => prev + 1);
      console.log(
        "Đã kích hoạt cập nhật trạng thái fanpage sau khi làm mới dữ liệu"
      );
    } catch (error) {
      console.error("Error refreshing fanpages:", error);
      toast.error("Không thể làm mới dữ liệu fanpage: " + error.message, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsRefreshingFanpages(false);
    }
  };

  // Hàm kiểm tra quyền Facebook
  const checkFacebookPermissions = async (accessToken) => {
    try {
      console.log("🔍 Checking Facebook permissions...");

      // Kiểm tra xem accessToken có phải là JWT token hay không
      if (accessToken.includes(".")) {
        console.error("❌ Access token is JWT format, not Facebook token");
        return {
          granted: [],
          missingRequired: FACEBOOK_COMMENT_REQUIRED_SCOPES,
          hasRequiredPermissions: false,
          error:
            "Invalid token format - JWT token provided instead of Facebook access token",
        };
      }

      return new Promise((resolve) => {
        window.FB.api(
          "/me/permissions",
          "get",
          { access_token: accessToken },
          function (response) {
            console.log("📋 User permissions:", response);

            if (response && response.data && !response.error) {
              const grantedPermissions = response.data
                .filter((perm) => perm.status === "granted")
                .map((perm) => perm.permission);

              const requiredPermissions = [
                ...FACEBOOK_COMMENT_REQUIRED_SCOPES,
                "business_management",
              ];

              const missingRequired = requiredPermissions.filter(
                (perm) => !grantedPermissions.includes(perm)
              );

              console.log("✅ Granted permissions:", grantedPermissions);
              console.log("❌ Missing required permissions:", missingRequired);

              resolve({
                granted: grantedPermissions,
                missingRequired: missingRequired,
                hasRequiredPermissions: missingRequired.length === 0,
              });
            } else {
              console.error("❌ Failed to check permissions:", response);
              resolve({
                granted: [],
                missingRequired: FACEBOOK_COMMENT_REQUIRED_SCOPES,
                hasRequiredPermissions: false,
                error: response?.error || "Unknown error",
              });
            }
          }
        );
      });
    } catch (error) {
      console.error("❌ Error checking permissions:", error);
      return {
        granted: [],
        missingRequired: FACEBOOK_COMMENT_REQUIRED_SCOPES,
        hasRequiredPermissions: false,
        error: error.message,
      };
    }
  };

  // Utility: wrap Promise với timeout để tránh treo mãi
  const withTimeout = (promise, timeoutMs, errorMsg = "Operation timed out") => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(errorMsg)), timeoutMs)
      ),
    ]);
  };

  // Hàm lấy dữ liệu fanpage từ Facebook API
  const fetchFanpagesFromFacebook = async () => {
    setIsLoadingFanpages(true);
    try {
      // Ưu tiên sử dụng facebookAccessToken nếu có
      const facebookAccessToken = localStorage.getItem("facebookAccessToken");
      const userAccessToken = localStorage.getItem("userAccessToken");

      // Sử dụng facebookAccessToken nếu có, nếu không thì kiểm tra userAccessToken
      let tokenToUse = facebookAccessToken;

      if (!tokenToUse && userAccessToken) {
        // Kiểm tra xem userAccessToken có phải là JWT token không
        if (userAccessToken.includes(".")) {
          console.log(
            "userAccessToken là JWT token, không thể sử dụng cho Facebook API"
          );
          toast.error("Cần kết nối với Facebook để lấy dữ liệu fanpage", {
            position: "top-right",
            autoClose: 5000,
            toastId: "fb-connect-required",
          });
          setNeedsFacebookConnect(true);
          setIsLoadingFanpages(false);
          return;
        }
        tokenToUse = userAccessToken;
      }

      if (!tokenToUse) {
        console.log("Không có token Facebook để lấy dữ liệu fanpage");
        setNeedsFacebookConnect(true);
        setIsLoadingFanpages(false);
        return;
      }

      await initFacebookSDK();
      console.log("Đang lấy dữ liệu fanpage từ Facebook API...");

      // Kiểm tra quyền trước khi gọi API
      const permissionCheck = await checkFacebookPermissions(tokenToUse);
      if (!permissionCheck.hasRequiredPermissions) {
        console.warn(
          "⚠️ Missing required permissions:",
          permissionCheck.missingRequired
        );

        // Kiểm tra xem có lỗi về token format không
        if (
          permissionCheck.error &&
          permissionCheck.error.includes("JWT token")
        ) {
          toast.error(
            "Token không hợp lệ cho Facebook API. Vui lòng kết nối lại với Facebook.",
            {
              position: "top-right",
              autoClose: 8000,
              toastId: "fb-token-invalid",
            }
          );
          setNeedsFacebookConnect(true);
          setIsLoadingFanpages(false);
          return;
        }

        toast.warn(
          `Thiếu quyền cần thiết: ${
            permissionCheck.missingRequired?.join(", ") || "Không xác định"
          }. Vui lòng cấp quyền lại.`,
          {
            toastId: "fb-missing-permissions",
            position: "top-right",
            autoClose: 8000,
          }
        );
        // Vẫn tiếp tục thử gọi API, có thể một số fanpage vẫn hiển thị được
      }

      // Debug logging cho API call
      console.log("🔍 Making /me/accounts API call with token:", {
        tokenLength: tokenToUse?.length || 0,
        tokenPreview: tokenToUse?.substring(0, 20) + "...",
        timestamp: new Date().toISOString(),
        permissions: permissionCheck.granted,
      });

      // Hàm lấy tất cả fanpages với pagination
      const getAllFanpages = async (
        accessToken,
        allPages = [],
        nextUrl = null
      ) => {
        return new Promise((resolve, reject) => {
          const apiCall = nextUrl || "/me/accounts";
          const params = nextUrl
            ? {}
            : {
                access_token: accessToken,
                fields: "id,name,access_token,category,tasks",
                limit: 100,
              };

          window.FB.api(apiCall, "get", params, async function (response) {
            console.log("📥 /me/accounts API response:", {
              success: !!response && !response.error,
              hasData: !!response?.data,
              dataLength: response?.data?.length || 0,
              hasPaging: !!response?.paging?.next,
              totalSoFar: allPages.length + (response?.data?.length || 0),
              error: response?.error || null,
              timestamp: new Date().toISOString(),
            });

            if (response && !response.error) {
              const currentPages = [...allPages, ...(response.data || [])];

              // Kiểm tra có trang tiếp theo không
              if (response.paging && response.paging.next) {
                console.log(
                  `🔄 Fetching next page... Currently have ${currentPages.length} pages`
                );
                // Gọi đệ quy để lấy trang tiếp theo
                const nextPageData = await getAllFanpages(
                  accessToken,
                  currentPages,
                  response.paging.next
                );
                resolve(nextPageData);
              } else {
                console.log(
                  `✅ Completed fetching all pages. Total: ${currentPages.length} fanpages`
                );
                resolve(currentPages);
              }
            } else {
              console.error("❌ Error fetching fanpages:", response?.error);
              reject(response?.error || new Error("Failed to fetch fanpages"));
            }
          });
        });
      };

      // Hàm lấy các fanpage thuộc Business Manager
      const getBusinessFanpages = async (accessToken) => {
        return new Promise((resolve, reject) => {
          console.log("🏢 Fetching Business Manager fanpages...");

          window.FB.api(
            "/me/businesses",
            "get",
            { access_token: accessToken, fields: "id,name", limit: 100 },
            async function (response) {
              if (response && !response.error) {
                const businesses = response.data || [];
                console.log(`📋 Found ${businesses.length} business managers`);
                const allBusinessPages = [];

                for (const biz of businesses) {
                  try {
                    console.log(
                      `🔄 Processing business: ${biz.name} (${biz.id})`
                    );

                    const pages = await new Promise((res, rej) => {
                      const fetchPages = (nextUrl, collected = []) => {
                        const apiCall = nextUrl || `/${biz.id}/owned_pages`;
                        const params = nextUrl
                          ? {}
                          : {
                              access_token: accessToken,
                              fields: "id,name,access_token,category,tasks",
                              limit: 100,
                            };

                        window.FB.api(apiCall, "get", params, function (resp) {
                          if (resp && !resp.error) {
                            const currentPages = [
                              ...collected,
                              ...(resp.data || []),
                            ];

                            console.log(
                              `📄 Fetched ${
                                resp.data?.length || 0
                              } pages from business ${biz.id}`
                            );

                            if (resp.paging && resp.paging.next) {
                              console.log(
                                `🔄 Fetching next page for business ${biz.id}...`
                              );
                              fetchPages(resp.paging.next, currentPages);
                            } else {
                              console.log(
                                `✅ Completed fetching pages for business ${biz.id}. Total: ${currentPages.length}`
                              );
                              res(currentPages);
                            }
                          } else {
                            console.error(
                              `❌ Error fetching pages for business ${biz.id}:`,
                              resp?.error
                            );
                            // Không reject ngay mà trả về empty array để tiếp tục với business khác
                            res([]);
                          }
                        });
                      };

                      fetchPages(null, []);
                    });

                    allBusinessPages.push(...pages);
                  } catch (err) {
                    console.error(
                      `❌ Error processing business ${biz.id}:`,
                      err
                    );
                    // Tiếp tục với business manager tiếp theo
                  }
                }

                console.log(
                  `🎉 Total business fanpages found: ${allBusinessPages.length}`
                );
                resolve(allBusinessPages);
              } else {
                console.error("❌ Error fetching businesses:", response?.error);
                // Nếu không có quyền business_management, trả về empty array thay vì reject
                if (
                  response?.error?.code === 200 ||
                  response?.error?.code === 10
                ) {
                  console.warn(
                    "⚠️ No business_management permission or no businesses found, continuing with personal pages only"
                  );
                  resolve([]);
                } else {
                  reject(
                    response?.error || new Error("Failed to fetch businesses")
                  );
                }
              }
            }
          );
        });
      };

      try {
        // Lấy tất cả fanpages từ tài khoản người dùng (timeout 15s)
        const userFanpages = await withTimeout(
          getAllFanpages(tokenToUse),
          15000,
          "Timeout khi lấy danh sách fanpage từ Facebook API"
        );

        // Lấy fanpage thuộc Business Manager nếu có quyền business_management
        let businessFanpages = [];
        try {
          // Kiểm tra quyền business_management trước khi gọi API
          if (permissionCheck.granted.includes("business_management")) {
            console.log(
              "✅ business_management permission granted, fetching business fanpages..."
            );
            businessFanpages = await withTimeout(
              getBusinessFanpages(tokenToUse),
              15000,
              "Timeout khi lấy fanpage từ Business Manager"
            );
            console.log(
              `🎉 Successfully fetched ${businessFanpages.length} business fanpages`
            );
          } else {
            console.warn(
              "⚠️ business_management permission not granted, skipping business fanpages"
            );
            toast.info(
              "Thiếu quyền Business Management. Chỉ hiển thị fanpage cá nhân. Để xem fanpage doanh nghiệp, vui lòng cấp quyền khi kết nối lại.",
              {
                position: "top-right",
                autoClose: 5000,
                toastId: "fb-missing-business-mgmt",
              }
            );
          }
        } catch (bmError) {
          console.error("Error fetching business fanpages:", bmError);
          toast.warning(
            "Không thể lấy fanpage từ Business Manager. Chỉ hiển thị fanpage cá nhân.",
            {
              position: "top-right",
              autoClose: 5000,
              toastId: "fb-business-fanpage-error",
            }
          );
        }

        // Kết hợp và loại bỏ trùng lặp fanpage
        const allFanpages = [...userFanpages, ...businessFanpages].reduce(
          (acc, page) => {
            if (!acc.some((p) => p.id === page.id)) {
              // Thêm metadata để biết fanpage này đến từ đâu
              page.source = userFanpages.includes(page)
                ? "personal"
                : "business";
              acc.push(page);
            }
            return acc;
          },
          []
        );

        console.log(
          `🎉 Successfully fetched ${allFanpages.length} total fanpages (${userFanpages.length} personal + ${businessFanpages.length} business)`
        );

        // Lưu tất cả fanpage vào state
        setAvailableFanpages(allFanpages);
        setFanpages(allFanpages);

        // Đồng bộ fanpage với Firebase
        const userId = localStorage.getItem("userId");
        if (userId) {
          const db = getDatabase();
          const connectedFanpagesRef = ref(
            db,
            `users/${userId}/connectedFanpages`
          );

          try {
            // Lấy dữ liệu hiện tại từ Firebase để giữ lại thông tin bổ sung
            const currentSnapshot = await get(connectedFanpagesRef);
            const currentFanpages = currentSnapshot.exists()
              ? currentSnapshot.val()
              : {};

            // Xử lý từng fanpage và chuyển đổi token
            const processedFanpages = [];
            const updatedFanpages = {};

            for (const page of allFanpages) {
              try {
                // Kiểm tra page có access_token hợp lệ không
                if (!page.access_token) {
                  console.warn(
                    `⚠️ Fanpage ${page.name} (${page.id}) không có access_token hợp lệ trong fetchFanpagesFromFacebook`,
                    page
                  );

                  // Tạo fanpage data với trạng thái lỗi
                  const fanpageData = {
                    id: page.id,
                    name: page.name,
                    access_token: null,
                    category: page.category || "",
                    lastRefreshed: Date.now(),
                    tokenStatus: "error",
                    tokenError: "Không có access_token từ Facebook API",
                    connectedAt:
                      currentFanpages[page.id]?.connectedAt || Date.now(),
                  };

                  updatedFanpages[page.id] = fanpageData;
                  processedFanpages.push(fanpageData);
                  continue; // Bỏ qua fanpage này và tiếp tục với fanpage khác
                }

                // Giữ lại thông tin hiện có nếu có
                const existingData = currentFanpages[page.id] || {};

                let fanpageData = {
                  ...existingData,
                  ...buildConnectedFanpageRecord(page, {
                    accessToken: page.access_token || null,
                    grantedScopes: currentFanpages[page.id]?.grantedScopes || [],
                    tokenStatus: "active",
                    tokenExpiry: existingData.tokenExpiry || null,
                  }),
                };

                // Nếu là fanpage mới, thêm thời gian kết nối
                if (!existingData.connectedAt) {
                  fanpageData.connectedAt = Date.now();
                }

                // Nếu không có tokenExpiry hoặc tokenExpiry đã hết hạn, đặt lại
                if (
                  !fanpageData.tokenExpiry ||
                  fanpageData.tokenExpiry < Date.now()
                ) {
                  fanpageData.tokenExpiry = null;
                }

                updatedFanpages[page.id] = fanpageData;
                processedFanpages.push(fanpageData);
              } catch (error) {
                console.error(`Lỗi xử lý fanpage ${page.name}:`, error);

                // Vẫn lưu fanpage với token gốc nếu có lỗi
                const fanpageData = buildConnectedFanpageRecord(page, {
                  accessToken: page.access_token || null,
                  grantedScopes: currentFanpages[page.id]?.grantedScopes || [],
                  tokenStatus: "error",
                  tokenError: error.message || "Unknown error",
                });

                // Nếu là fanpage mới, thêm thời gian kết nối
                if (!currentFanpages[page.id]) {
                  fanpageData.connectedAt = Date.now();
                } else if (currentFanpages[page.id].connectedAt) {
                  fanpageData.connectedAt =
                    currentFanpages[page.id].connectedAt;
                }

                updatedFanpages[page.id] = fanpageData;
                processedFanpages.push(fanpageData);
              }
            }

            // Sanitize dữ liệu trước khi update để tránh undefined values
            const sanitizedUpdatedFanpages =
              sanitizeForFirebase(updatedFanpages);

            // Debug: Kiểm tra dữ liệu trước khi update
            console.log("🔍 Dữ liệu trước khi sanitize:", updatedFanpages);
            console.log(
              "✅ Dữ liệu sau khi sanitize:",
              sanitizedUpdatedFanpages
            );

            // Cập nhật database
            await update(connectedFanpagesRef, sanitizedUpdatedFanpages);
            await update(
              ref(db, `users/${userId}/connectedChannels/facebook`),
              buildFacebookConnectedChannelMap(sanitizedUpdatedFanpages)
            );

            console.log(
              `✅ Đã cập nhật dữ liệu ${processedFanpages.length} fanpage từ Facebook API`
            );

            // Cập nhật state connectedFanpages sau khi lưu vào Firebase
            setConnectedFanpages(processedFanpages);
            setFanpages(processedFanpages);

            // Reset needsFacebookConnect flag khi thành công
            setNeedsFacebookConnect(false);

            // Lưu timestamp refresh thành công vào localStorage
            localStorage.setItem("lastFanpageRefreshTime", Date.now().toString());
            console.log("✅ Đã lưu lastFanpageRefreshTime vào localStorage");

            console.log(
              `🔄 State updated - connectedFanpages.length: ${processedFanpages.length}`
            );
            console.log("📊 Current state after update:", {
              connectedFanpagesLength: processedFanpages.length,
              isLoadingFanpages: false,
              isRefreshingFanpages: false,
              needsFacebookConnect: false,
            });

            // Hiển thị thông báo thành công
            toast.success(
              `🎉 Đã tải thành công ${allFanpages.length} fanpages!`,
              {
                position: "top-right",
                autoClose: 3000,
              }
            );
            setIsLoadingFanpages(false);
          } catch (error) {
            console.error("Error processing fanpages:", error);
            toast.error(
              "Không thể cập nhật thông tin fanpage: " + error.message,
              {
                position: "top-right",
                autoClose: 5000,
              }
            );
            setIsLoadingFanpages(false);
          }
        }
      } catch (error) {
        console.error("❌ Error getting all fanpages:", error);

        // Xử lý lỗi khi lấy fanpages
        let errorMessage = "Không thể lấy danh sách fanpage!";
        if (error?.message) {
          errorMessage = `Lỗi: ${error.message}`;
        }

        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          toastId: "fb-fanpage-list-error",
        });

        setIsLoadingFanpages(false);
        return;
      }
    } catch (error) {
      console.error("❌ Error in fetchFanpagesFromFacebook:", error);

      let errorMessage = "Không thể lấy danh sách fanpage!";
      if (error?.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }

      // Nếu lỗi timeout SDK → hiển thị thông báo kết nối Facebook
      if (error?.message?.includes("timed out")) {
        setNeedsFacebookConnect(true);
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        toastId: "fb-fanpage-fetch-error",
      });
    } finally {
      // Đảm bảo LUÔN reset loading state dù có lỗi hay không
      setIsLoadingFanpages(false);
    }
  };

  // Hàm xử lý lỗi gốc của Facebook API (helper function)
  const handleOriginalError = (response) => {
    if (response && response.error) {
      console.error("❌ Facebook API Error:", response.error);

      // Xử lý các loại lỗi cụ thể
      let errorMessage = "Không thể lấy thông tin fanpage từ Facebook!";
      if (response.error) {
        switch (response.error.code) {
          case 200:
            errorMessage =
              "Thiếu quyền truy cập Pages. Vui lòng cấp quyền 'pages_show_list' cho ứng dụng.";
            break;
          case 190:
            errorMessage =
              "Token đã hết hạn hoặc không hợp lệ. Vui lòng đăng nhập lại.";
            break;
          case 10:
            errorMessage =
              "Ứng dụng không có quyền truy cập API này. Vui lòng liên hệ admin.";
            break;
          case 102:
            errorMessage =
              "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.";
            break;
          default:
            errorMessage = `Lỗi Facebook API: ${
              response.error.message || "Unknown error"
            }`;
        }
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 7000,
        toastId: "fb-api-error",
      });
    }
  };

  // Load dữ liệu fanpage từ Firebase cache khi vào màn hình
  // Sau khi load cache, kiểm tra nếu đã > 1 ngày thì auto-refresh từ Facebook API
  useEffect(() => {
    const loadFanpagesFromCache = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        // Đọc từ Firebase cache trước
        const db = getDatabase();
        const connectedFanpagesRef = ref(
          db,
          `users/${userId}/connectedFanpages`
        );
        const snapshot = await get(connectedFanpagesRef);

        if (snapshot.exists()) {
          const connectedData = snapshot.val();
          const fanpagesArray = Object.values(connectedData);

          setConnectedFanpages(fanpagesArray);
          setFanpages(fanpagesArray);

          if (fanpagesArray.length > 0) {
            setNeedsFacebookConnect(false);
            setSelectedFanpage(fanpagesArray[0].id);
          }

          console.log(`✅ Loaded ${fanpagesArray.length} fanpages from Firebase cache`);

          // Kiểm tra xem có cần auto-refresh không (đã qua 1 ngày kể từ lần refresh cuối)
          // Chỉ auto-refresh nếu user CÓ Facebook token (tránh lỗi cho Google login users)
          const facebookAccessToken = localStorage.getItem("facebookAccessToken");
          const userAccessToken = localStorage.getItem("userAccessToken");
          const hasFBToken = facebookAccessToken || (userAccessToken && !userAccessToken.includes("."));

          if (hasFBToken && shouldRefreshFanpages(fanpagesArray)) {
            console.log("🔄 Auto-refresh: đã > 1 ngày → gọi Facebook API để cập nhật fanpages...");
            fetchFanpagesFromFacebook();
          } else if (!hasFBToken) {
            console.log("⏳ User không có Facebook token (Google login) → dùng dữ liệu cache, skip FB API call");
          } else {
            console.log("⏳ Chưa đủ 1 ngày → dùng dữ liệu cache, skip FB API call");
          }
        } else {
          // Không có fanpage trong cache — kiểm tra xem có cần hiện nút "Kết nối Facebook" không
          const facebookAccessToken = localStorage.getItem("facebookAccessToken");
          const userAccessToken = localStorage.getItem("userAccessToken");
          const hasFBToken = facebookAccessToken || (userAccessToken && !userAccessToken.includes("."));

          if (!hasFBToken) {
            setNeedsFacebookConnect(true);
          }
          console.log("ℹ️ Không có fanpage trong cache. User cần click 'Thêm Fanpage' để lấy từ Facebook.");
        }
      } catch (error) {
        console.error("Error loading fanpages from cache:", error);
      }
    };

    loadFanpagesFromCache();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const id = localStorage.getItem("userId");
        setUserId(id);
        const userId = id;
        const db = getDatabase();

        // Kiểm tra xem người dùng có token Facebook hay không
        const userAccessToken = localStorage.getItem("userAccessToken");
        const facebookAccessToken = localStorage.getItem("facebookAccessToken");
        const facebookAuthRef = ref(db, `users/${userId}/facebookAuth`);
        const facebookAuthSnapshot = await get(facebookAuthRef);

        // Kiểm tra xem userAccessToken có phải là Facebook token không (không phải JWT)
        let hasValidFacebookToken = false;

        if (facebookAccessToken) {
          hasValidFacebookToken = true;
        } else if (userAccessToken && !userAccessToken.includes(".")) {
          // userAccessToken không phải JWT và có thể là Facebook token
          hasValidFacebookToken = true;
        } else if (
          facebookAuthSnapshot.exists() &&
          facebookAuthSnapshot.val().userAccessToken
        ) {
          // Có token trong database
          hasValidFacebookToken = true;
        }

        if (!hasValidFacebookToken) {
          console.log(
            "Người dùng chưa có token Facebook hợp lệ, cần kết nối Facebook"
          );
          setNeedsFacebookConnect(true);
        } else {
          console.log("Người dùng đã có token Facebook, không cần kết nối lại");
          setNeedsFacebookConnect(false);
        }

        // Lấy email từ root của user (nếu có)
        const rootUserRef = ref(db, `users/${userId}`);
        const rootUserSnapshot = await get(rootUserRef);
        let userEmail = "";

        if (rootUserSnapshot.exists()) {
          const rootData = rootUserSnapshot.val();
          if (rootData.email) {
            userEmail = rootData.email;
          }
          if (Array.isArray(rootData.providers)) {
            setAuthProviders(rootData.providers);
          }
          if (typeof rootData.hasPassword === "boolean") {
            setHasPassword(rootData.hasPassword);
          }
        }

        if (userEmail) {
          try {
            const auth = getAuth(app);
            const signInMethods = await fetchSignInMethodsForEmail(auth, userEmail);
            const providersFromMethods = [];
            if (signInMethods.includes("google.com")) providersFromMethods.push("google");
            if (signInMethods.includes("facebook.com")) providersFromMethods.push("facebook");
            if (signInMethods.includes("password")) providersFromMethods.push("password");
            if (providersFromMethods.length > 0) {
              setAuthProviders((prev) =>
                [...new Set([...(Array.isArray(prev) ? prev : []), ...providersFromMethods])]
              );
            }
            if (signInMethods.includes("password")) {
              setHasPassword(true);
            }
          } catch (signInMethodError) {
            console.warn("Không thể đọc sign-in methods:", signInMethodError);
          }
        }

        // Lấy thông tin từ profile
        const userRef = ref(db, `users/${userId}/profile`);
        const snapshot = await get(userRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          // Ưu tiên email từ root nếu có, nếu không thì lấy từ profile
          setEmail(userEmail || data.email || "");
          setPhone(data.phone || "");
          setReferralPhone(data.referralPhone || "");
          if (data.referralPhone) {
            const input = document.querySelector('input[name="referralPhone"]');
            if (input) input.disabled = true;
          }
        } else {
          // Nếu không có profile nhưng có email ở root
          setEmail(userEmail || "");
        }

        const pointsRef = ref(db, `users/${userId}/points`);
        const pointsSnapshot = await get(pointsRef);
        if (pointsSnapshot.exists()) {
          const data = pointsSnapshot.val();
          if (data.points !== undefined && data.purchasedPoints !== undefined) {
            setPointsDetail({
              points: data.points,
              purchasedPoints: data.purchasedPoints,
              lastUpdated: data.lastUpdated || Date.now(),
            });
          } else if (typeof data.points === "number") {
            setPointsDetail({
              points: data.points,
              purchasedPoints: 0,
              lastUpdated: Date.now(),
            });
          }
        }

        const membershipRef = ref(db, `users/${userId}/membership`);
        const expiryRef = ref(db, `users/${userId}/membershipExpiry`);
        const typeRef = ref(db, `users/${userId}/membershipType`);
        const isTrialRef = ref(db, `users/${userId}/isTrial`);

        const membershipSnapshot = await get(membershipRef);
        const expirySnapshot = await get(expiryRef);
        const typeSnapshot = await get(typeRef);
        const isTrialSnapshot = await get(isTrialRef);
        setIsTrial(isTrialSnapshot.exists() ? isTrialSnapshot.val() : false);

        if (membershipSnapshot.exists() || expirySnapshot.exists()) {
          // Normalize membership to number — admin PATCH may store as string "3"
          let membershipVal = membershipSnapshot.exists() ? membershipSnapshot.val() : null;
          if (typeof membershipVal === "string") {
            const parsed = parseInt(membershipVal, 10);
            membershipVal = isNaN(parsed) ? membershipVal : parsed;
          }
          setMembershipInfo({
            currentMembership: membershipVal,
            expiryDate: expirySnapshot.exists() ? expirySnapshot.val() : null,
            isYearly: typeSnapshot.exists()
              ? typeSnapshot.val() === "yearly" ||
                typeSnapshot.val() === "annual"
              : false,
          });
        }

        const wordpressSitesRef = ref(db, `users/${userId}/wordpressSites`);
        const wordpressSitesSnapshot = await get(wordpressSitesRef);
        if (wordpressSitesSnapshot.exists()) {
          const sitesData = wordpressSitesSnapshot.val();
          const sitesArray = Object.keys(sitesData).map((key) => ({
            id: key,
            ...sitesData[key],
          }));
          setWordpressSites(sitesArray);
        }

        // Không cần lấy connectedFanpages ở đây nữa vì đã được xử lý trong checkAndFetchFanpages
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (!userId) return;
    loadZaloAccounts(userId);
  }, [userId, loadZaloAccounts]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const zaloSuccess = params.get("zalo_success");
    const zaloError = params.get("zalo_error");

    if (zaloSuccess === "true") {
      toast.success("Kết nối Zalo OA thành công", {
        position: "top-right",
        autoClose: 3000,
      });
      const targetUserId = localStorage.getItem("userId");
      if (targetUserId) {
        loadZaloAccounts(targetUserId);
      }
    } else if (zaloError) {
      toast.error(`Kết nối Zalo thất bại: ${zaloError}`, {
        position: "top-right",
        autoClose: 6000,
      });
    }

    if (zaloSuccess || zaloError) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadZaloAccounts]);

  const handleSave = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const db = getDatabase();

      const userRef = ref(db, `users/${userId}/profile`);
      const snapshot = await get(userRef);
      const existingData = snapshot.exists() ? snapshot.val() : {};

      if (
        existingData.referralPhone &&
        existingData.referralPhone !== referralPhone
      ) {
        toast.error(
          "Không thể thay đổi số điện thoại giới thiệu sau khi đã lưu!",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        setReferralPhone(existingData.referralPhone);
        return;
      }

      // Lưu thông tin vào profile
      await set(userRef, {
        email,
        phone,
        referralPhone,
        updatedAt: new Date().toISOString(),
      });

      // Lưu email vào root của user để đồng bộ
      if (email) {
        const rootUserRef = ref(db, `users/${userId}`);
        await update(rootUserRef, {
          email: email,
        });
      }

      if (referralPhone && !existingData.referralPhone) {
        const input = document.querySelector('input[name="referralPhone"]');
        if (input) input.disabled = true;
      }

      toast.success("Lưu thông tin thành công!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Lưu thông tin thất bại", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleSetupPassword = async () => {
    if (newPassword.length < 6) {
      toast.error("Mật khẩu cần tối thiểu 6 ký tự.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Mật khẩu xác nhận không khớp.");
      return;
    }

    setIsSettingPassword(true);
    try {
      const auth = getAuth(app);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("USER_NOT_AUTHENTICATED");
      }

      const accountEmail = (currentUser.email || "").trim().toLowerCase();
      const profileEmail = (email || "").trim().toLowerCase();
      const normalizedEmail = accountEmail || profileEmail;
      if (!normalizedEmail) {
        throw new Error("USER_EMAIL_MISSING");
      }

      if (profileEmail && accountEmail && profileEmail !== accountEmail) {
        toast.info(
          "Hệ thống dùng email đăng nhập Google hiện tại để thiết lập mật khẩu."
        );
      }

      const credential = EmailAuthProvider.credential(normalizedEmail, newPassword);
      try {
        await linkWithCredential(currentUser, credential);
      } catch (linkError) {
        if (linkError.code === "auth/requires-recent-login") {
          const provider = new GoogleAuthProvider();
          await reauthenticateWithPopup(currentUser, provider);
          await linkWithCredential(currentUser, credential);
        } else {
          throw linkError;
        }
      }

      const db = getDatabase();
      const rootUserRef = ref(db, `users/${userId}`);
      const providersFromAuth = (currentUser.providerData || [])
        .map((providerInfo) => {
          if (providerInfo?.providerId === "google.com") return "google";
          if (providerInfo?.providerId === "facebook.com") return "facebook";
          if (providerInfo?.providerId === "password") return "password";
          return providerInfo?.providerId || null;
        })
        .filter(Boolean);

      await update(rootUserRef, {
        email: normalizedEmail,
        hasPassword: true,
        providers: [
          ...new Set([...(authProviders || []), ...providersFromAuth, "password"]),
        ],
      });

      setHasPassword(true);
      setAuthProviders((prev) => [
        ...new Set([...(prev || []), ...providersFromAuth, "password"]),
      ]);
      setEmail(normalizedEmail);
      setNewPassword("");
      setConfirmPassword("");

      toast.success("Thiết lập mật khẩu thành công. Bạn có thể đăng nhập bằng email/mật khẩu.");
    } catch (error) {
      console.error("Error setting password:", error);
      if (error.message === "USER_NOT_AUTHENTICATED") {
        toast.error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
      } else if (error.message === "USER_EMAIL_MISSING") {
        toast.error("Không tìm thấy email đăng nhập của tài khoản hiện tại.");
      } else if (error.code === "auth/provider-already-linked") {
        setHasPassword(true);
        toast.info("Tài khoản này đã có mật khẩu trước đó.");
      } else if (error.code === "auth/credential-already-in-use") {
        toast.error("Thông tin đăng nhập này đã được dùng cho tài khoản khác.");
      } else if (error.code === "auth/email-already-in-use") {
        toast.error("Email đã tồn tại ở tài khoản khác.");
      } else if (error.code === "auth/weak-password") {
        toast.error("Mật khẩu quá yếu. Vui lòng dùng mật khẩu mạnh hơn.");
      } else if (error.code === "auth/popup-closed-by-user") {
        toast.error("Bạn đã đóng cửa sổ xác thực lại. Vui lòng thử lại.");
      } else if (error.code === "auth/operation-not-allowed") {
        toast.error(
          "Dự án Firebase chưa bật Email/Password trong Authentication > Sign-in method."
        );
      } else {
        toast.error(
          `Thiết lập mật khẩu thất bại: ${error.code || error.message || "Lỗi không xác định"}`
        );
      }
    } finally {
      setIsSettingPassword(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error("Vui lòng nhập mật khẩu hiện tại.");
      return;
    }
    if (changePasswordValue.length < 6) {
      toast.error("Mật khẩu mới cần tối thiểu 6 ký tự.");
      return;
    }
    if (changePasswordValue !== confirmChangePassword) {
      toast.error("Mật khẩu mới và xác nhận mật khẩu chưa khớp.");
      return;
    }

    setIsChangingPassword(true);
    try {
      const auth = getAuth(app);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("USER_NOT_AUTHENTICATED");
      }

      const normalizedEmail = (currentUser.email || email || "").trim().toLowerCase();
      if (!normalizedEmail) {
        throw new Error("USER_EMAIL_MISSING");
      }

      const credential = EmailAuthProvider.credential(normalizedEmail, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, changePasswordValue);

      setCurrentPassword("");
      setChangePasswordValue("");
      setConfirmChangePassword("");
      toast.success("Đổi mật khẩu thành công.");
    } catch (error) {
      console.error("Error changing password:", error);
      if (error.message === "USER_NOT_AUTHENTICATED") {
        toast.error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
      } else if (error.code === "auth/invalid-credential" || error.code === "auth/wrong-password") {
        toast.error("Mật khẩu hiện tại chưa đúng.");
      } else if (error.code === "auth/weak-password") {
        toast.error("Mật khẩu mới quá yếu.");
      } else if (error.code === "auth/too-many-requests") {
        toast.error("Bạn thao tác quá nhiều lần. Vui lòng thử lại sau.");
      } else if (error.code === "auth/requires-recent-login") {
        toast.error("Vui lòng đăng nhập lại trước khi đổi mật khẩu.");
      } else {
        toast.error(
          `Đổi mật khẩu thất bại: ${error.code || error.message || "Lỗi không xác định"}`
        );
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSiteInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSite((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddNewSite = () => {
    setCurrentSite({
      id: null,
      url: "",
      username: "",
      password: "",
      siteName: "",
      websiteInfo: "",
      contactInfo: "",
    });
    setIsEditingSite(true);
    setShowPassword(false);
  };

  const handleEditSite = (site) => {
    setCurrentSite({ ...site });
    setIsEditingSite(true);
    setShowPassword(false);
  };

  const handleSaveSite = async () => {
    if (
      !currentSite.url ||
      !currentSite.username ||
      !currentSite.password ||
      !currentSite.siteName
    ) {
      toast.error("Vui lòng nhập đầy đủ thông tin website!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Kiểm tra định dạng URL cơ bản
    try {
      new URL(currentSite.url);
    } catch (e) {
      toast.error("URL không hợp lệ. Vui lòng kiểm tra lại!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Hiển thị thông báo bắt đầu kiểm tra
    setIsTestingConnection(true);
    setConnectionStatus(null);

    toast.info("Đang kiểm tra kết nối đến WordPress...", {
      position: "top-right",
      autoClose: 2000,
    });

    try {
      // Kiểm tra kết nối WordPress trước khi lưu
      console.log("🔄 Bắt đầu kiểm tra kết nối WordPress...");
      const connectionResult = await testWordPressConnection(currentSite);

      if (!connectionResult.success) {
        setIsTestingConnection(false);
        setConnectionStatus({
          success: false,
          message: connectionResult.message,
        });

        toast.error(
          `Lỗi kết nối WordPress: ${connectionResult.message}. Vui lòng kiểm tra lại thông tin.`,
          {
            position: "top-right",
            autoClose: 8000,
          }
        );
        return;
      }

      // Nếu kết nối thành công, hiển thị thông báo và tiếp tục lưu
      setConnectionStatus({
        success: true,
        message: connectionResult.message,
        user: connectionResult.user,
      });

      toast.success(connectionResult.message, {
        position: "top-right",
        autoClose: 3000,
      });

      // Bắt đầu kiểm tra SEO Connector
      toast.info("Đang kiểm tra SEO Connector...", {
        position: "top-right",
        autoClose: 2000,
      });
      
      let connectorInstalled = false;
      let connectorVersion = null;
      
      try {
        const connectorResult = await checkSeoConnector(currentSite);
        if (connectorResult.success && connectorResult.data.installed) {
          connectorInstalled = true;
          connectorVersion = connectorResult.data.pluginVersion;
          toast.success("SEO Connector đã sẵn sàng!", {
            position: "top-right",
            autoClose: 3000,
          });
        } else {
          toast.warning("Chưa cài đặt SEO Connector. Tính năng đồng bộ Meta SEO sẽ bị vô hiệu hóa.", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } catch (error) {
        toast.warning("Không thể kiểm tra SEO Connector. Tính năng đồng bộ Meta SEO có thể bị vô hiệu hóa.", {
          position: "top-right",
          autoClose: 5000,
        });
      }

      // Tiếp tục lưu vào Firebase
      const userId = localStorage.getItem("userId");
      const db = getDatabase();
      const wordpressSitesRef = ref(db, `users/${userId}/wordpressSites`);

      const normalizedSiteUrl = normalizeWordPressSiteUrl(currentSite.url);

      if (currentSite.id) {
        const siteRef = ref(
          db,
          `users/${userId}/wordpressSites/${currentSite.id}`
        );
        await update(siteRef, {
          url: normalizedSiteUrl,
          username: currentSite.username,
          password: currentSite.password,
          siteName: currentSite.siteName,
          websiteInfo: currentSite.websiteInfo,
          contactInfo: currentSite.contactInfo || "",
          seoConnectorInstalled: connectorInstalled,
          seoConnectorVersion: connectorVersion,
          updatedAt: new Date().toISOString(),
        });
      } else {
        const newSiteRef = push(wordpressSitesRef);
        await set(newSiteRef, {
          url: normalizedSiteUrl,
          username: currentSite.username,
          password: currentSite.password,
          siteName: currentSite.siteName,
          createdAt: new Date().toISOString(),
          websiteInfo: currentSite.websiteInfo,
          contactInfo: currentSite.contactInfo || "",
          seoConnectorInstalled: connectorInstalled,
          seoConnectorVersion: connectorVersion,
          updatedAt: new Date().toISOString(),
        });
      }

      const refreshSnapshot = await get(wordpressSitesRef);
      if (refreshSnapshot.exists()) {
        const sitesData = refreshSnapshot.val();
        const sitesArray = Object.keys(sitesData).map((key) => ({
          id: key,
          ...sitesData[key],
        }));
        setWordpressSites(sitesArray);
      }

      setIsEditingSite(false);
      setCurrentSite({
        id: null,
        url: "",
        username: "",
        password: "",
        siteName: "",
        websiteInfo: "",
        contactInfo: "",
      });
      setConnectionStatus(null);

      // Auto-populate publisher (E-E-A-T) từ thông tin website KH
      // Best-effort: không block flow nếu thất bại
      try {
        const auth = getAuth(app);
        const firebaseUser = auth.currentUser;
        if (firebaseUser) {
          const token = await firebaseUser.getIdToken();
          const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3010";
          await axios.post(
            `${apiUrl}/api/user/publisher/auto-populate`,
            { siteName: currentSite.siteName, url: normalizedSiteUrl },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      } catch (autoPopErr) {
        // Silent fail — auto-populate là best-effort, không ảnh hưởng save site
        console.warn("Auto-populate publisher failed:", autoPopErr.message);
      }

      toast.success("Lưu thông tin website thành công!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error during WordPress connection test or saving:", error);
      setConnectionStatus({
        success: false,
        message: "Lỗi khi kiểm tra kết nối hoặc lưu thông tin website",
      });

      toast.error(
        "Đã xảy ra lỗi: " +
          (error.message || "Không thể lưu thông tin website"),
        {
          position: "top-right",
          autoClose: 8000,
        }
      );
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleCheckConnector = async (site) => {
    setCheckingConnector(true);
    toast.info("Đang kiểm tra lại SEO Connector...", { autoClose: 2000 });
    
    try {
      const result = await checkSeoConnector(site);
      if (result.success && result.data.installed) {
        toast.success("Tuyệt vời! SEO Connector đã được cài đặt thành công.", { autoClose: 4000 });
        
        // Cập nhật Firebase
        const userId = localStorage.getItem("userId");
        const db = getDatabase();
        const siteRef = ref(db, `users/${userId}/wordpressSites/${site.id}`);
        await update(siteRef, {
          seoConnectorInstalled: true,
          seoConnectorVersion: result.data.pluginVersion,
          updatedAt: new Date().toISOString()
        });
        
        // Cập nhật state local
        setWordpressSites(sites => sites.map(s => 
          s.id === site.id 
            ? { ...s, seoConnectorInstalled: true, seoConnectorVersion: result.data.pluginVersion } 
            : s
        ));
        
        setShowConnectorDialog(false);
      } else {
        toast.error("Chưa phát hiện thấy SEO Connector. Vui lòng đảm bảo bạn đã cài và Activate plugin.", { autoClose: 5000 });
      }
    } catch (error) {
      // toast.error error handled inside checkSeoConnector
      console.error(error);
    } finally {
      setCheckingConnector(false);
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa website này?")) {
      return;
    }

    try {
      const userId = localStorage.getItem("userId");
      const db = getDatabase();
      const siteRef = ref(db, `users/${userId}/wordpressSites/${siteId}`);

      await remove(siteRef);

      setWordpressSites((prev) => prev.filter((site) => site.id !== siteId));

      toast.success("Xóa website thành công!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error deleting WordPress site:", error);
      toast.error("Xóa website thất bại!", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleCopyUserId = () => {
    navigator.clipboard.writeText(userId).then(() => {
      toast.success("Đã sao chép ID người dùng!", {
        position: "top-right",
        autoClose: 3000,
      });
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleCancelEdit = () => {
    setIsEditingSite(false);
    setCurrentSite({
      id: null,
      url: "",
      username: "",
      password: "",
      siteName: "",
      websiteInfo: "",
      contactInfo: "",
    });
    setConnectionStatus(null);
    setIsTestingConnection(false);
  };

  const handleWordPressGuide = () => {
    window.open(
      "https://youtu.be/Venh30_6NV0?si=qXT7_zROU3UZNpQI",
      "_blank",
      "noopener,noreferrer"
    );
    toast.success("Đã mở video hướng dẫn thiết lập WordPress!", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const handleMetaDescriptionGuide = () => {
    window.open(
      "https://youtu.be/bw7zG8mOe-g",
      "_blank",
      "noopener,noreferrer"
    );
    toast.success("Đã mở video hướng dẫn thiết lập Meta Description!", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  // Hàm xử lý kết nối Facebook khi người dùng đăng nhập bằng Google
  const handleFacebookConnect = async () => {
    // Kiểm tra cấu hình trước
    if (!checkFacebookAppConfig()) {
      return;
    }

    setIsLoadingFanpages(true);
    try {
      // Kiểm tra và làm mới Facebook token trước khi kết nối Facebook
      const tokenRefreshed = await checkAndRefreshUserToken();

      // Nếu token đã được làm mới thành công, không cần đăng nhập lại
      if (tokenRefreshed) {
        console.log("Token đã được làm mới, không cần đăng nhập lại");
        setIsLoadingFanpages(false);
        setNeedsFacebookConnect(false);

        // Thông báo cho người dùng
        toast.success("Kết nối Facebook thành công!", {
          position: "top-right",
          autoClose: 3000,
        });

        return;
      }

      // Nếu không thể làm mới token, tiếp tục với quy trình đăng nhập mới
      console.log(
        "Không thể làm mới token, tiếp tục với quy trình đăng nhập mới"
      );

      // Kiểm tra FB SDK đã init chưa
      if (!window.FB || !window.fbInitialized) {
        console.warn("⚠️ FB SDK chưa init, dùng redirect flow");
        startFBRedirectLogin("connect_facebook");
        return;
      }

      fbLoginWithTimeout(
        {
          scope: FACEBOOK_MANAGEMENT_SCOPE,
          auth_type: "rerequest",
        },
        // onSuccess
        (response) => {
          const shortLivedToken = response.authResponse.accessToken;
          const userID = localStorage.getItem("userId");
          fetch(
            `https://graph.facebook.com/v21.0/oauth/access_token?grant_type=fb_exchange_token&client_id=${process.env.REACT_APP_FB_CLIENT_ID}&client_secret=${process.env.REACT_APP_FB_CLIENT_SECRET}&fb_exchange_token=${shortLivedToken}`
          )
            .then((res) => res.json())
            .then((data) => {
              if (data.access_token) {
                const accessToken = data.access_token;
                const expiryTime = Date.now() + data.expires_in * 1000;
                localStorage.setItem("facebookAccessToken", accessToken);
                localStorage.setItem("facebookAccessTokenExpiry", expiryTime);
                const currentLoginMethod = localStorage.getItem("loginMethod") || "unknown";
                if (currentLoginMethod === "unknown") localStorage.setItem("loginMethod", "facebook");
                if (currentLoginMethod === "facebook") {
                  localStorage.setItem("userAccessToken", accessToken);
                  localStorage.setItem("userAccessTokenExpiry", expiryTime);
                }
                const db = getDatabase();
                const userTokenRef = ref(db, `users/${userID}/facebookAuth`);
                const validExpiryTime = !isNaN(expiryTime) ? expiryTime : Date.now() + 60 * 24 * 60 * 60 * 1000;
                fetchGrantedFacebookScopes(accessToken).then((grantedScopes) =>
                  update(userTokenRef, {
                  userAccessToken: accessToken,
                  userAccessTokenExpiry: validExpiryTime,
                  tokenType: "long_lived_user_token",
                  lastUpdated: Date.now(),
                    grantedScopes,
                  })
                )
                  .then(() => {
                    console.log("✅ Đã lưu Facebook token vào Firebase Realtime DB");
                    setNeedsFacebookConnect(false);
                    setIsLoadingFanpages(false);
                    toast.success("Kết nối Facebook thành công!", { position: "top-right", autoClose: 3000 });
                  })
                  .catch((tokenError) => {
                    setIsLoadingFanpages(false);
                    console.error("❌ Lỗi khi lưu token vào Firebase:", tokenError);
                    toast.error("Lỗi khi lưu token: " + tokenError.message, { position: "top-right", autoClose: 5000 });
                  });
              } else {
                setIsLoadingFanpages(false);
                toast.error("Không thể lấy access token từ Facebook", { position: "top-right", autoClose: 5000 });
              }
            })
            .catch((error) => {
              setIsLoadingFanpages(false);
              toast.error("Lỗi khi trao đổi token: " + error.message, { position: "top-right", autoClose: 5000 });
            });
        },
        // onFail
        () => {
          setIsLoadingFanpages(false);
          toast.error("Kết nối Facebook không thành công!", { position: "top-right", autoClose: 5000 });
        },
        {
          fallbackPendingAction: "connect_facebook",
        }
      );
    } catch (error) {
      setIsLoadingFanpages(false);
      console.error("Error connecting to Facebook:", error);
      toast.error("Đã xảy ra lỗi khi kết nối với Facebook: " + error.message, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  // Kết nối fanpage mới và lưu thông tin giống với checkAndRefreshFanpageToken
  const handleConnectNewPages = () => {
    setIsAddingFanpage(true);
    try {
      console.log("🔄 Starting new fanpage connection process...");

      // QUAN TRỌNG: Không được dùng await/async trước FB.login()!
      // Nếu có await, browser sẽ chặn popup vì mất user gesture context.

      // Kiểm tra FB SDK đã sẵn sàng chưa — nếu chưa, dùng redirect flow ngay
      if (!window.FB || !window.fbInitialized) {
        console.warn("⚠️ FB SDK chưa init, dùng redirect flow");
        startFBRedirectLogin("connect_fanpage");
        return;
      }

      // KHÔNG gọi FB.logout() rồi delay rồi mới FB.login()
      // Vì: (1) FB.logout() phá session hiện tại, (2) delay phá user gesture context → popup bị chặn
      // FB.login() với auth_type: "rerequest" đã đủ để force user chọn lại permissions

      toast.info(
        "Vui lòng chọn lại danh sách fanpage bạn muốn kết nối trong popup Facebook sắp hiển thị.",
        { position: "top-right", autoClose: 5000 }
      );

      console.log("🚀 Initiating Facebook login (no logout/delay to preserve user gesture)...");
      fbLoginWithTimeout(
        {
          scope: FACEBOOK_MANAGEMENT_SCOPE,
          auth_type: "rerequest",
        },
        // onSuccess
        (response) => {
          const shortLivedToken = response.authResponse.accessToken;
          const userID = localStorage.getItem("userId");
          localStorage.setItem("facebookAccessToken", shortLivedToken);
          const currentLoginMethod = localStorage.getItem("loginMethod") || "unknown";
          if (currentLoginMethod === "unknown") localStorage.setItem("loginMethod", "facebook");
          if (currentLoginMethod === "facebook") localStorage.setItem("userAccessToken", shortLivedToken);

          fetch(
            `https://graph.facebook.com/v21.0/oauth/access_token?grant_type=fb_exchange_token&client_id=${process.env.REACT_APP_FB_CLIENT_ID}&client_secret=${process.env.REACT_APP_FB_CLIENT_SECRET}&fb_exchange_token=${shortLivedToken}`
          )
            .then((res) => res.json())
            .then((data) => {
              if (data.access_token) {
                const accessToken = data.access_token;
                const expiryTime = Date.now() + data.expires_in * 1000;
                localStorage.setItem("facebookAccessToken", accessToken);
                localStorage.setItem("facebookAccessTokenExpiry", expiryTime);
                if (currentLoginMethod === "facebook") {
                  localStorage.setItem("userAccessToken", accessToken);
                  localStorage.setItem("userAccessTokenExpiry", expiryTime);
                }
                const db = getDatabase();
                const userTokenRef = ref(db, `users/${userID}/facebookAuth`);
                const validExpiryTime = !isNaN(expiryTime) ? expiryTime : Date.now() + 60 * 24 * 60 * 60 * 1000;
                fetchGrantedFacebookScopes(accessToken).then((grantedScopes) =>
                  update(userTokenRef, {
                  userAccessToken: accessToken,
                  userAccessTokenExpiry: validExpiryTime,
                  tokenType: "long_lived_user_token",
                  lastUpdated: Date.now(),
                    grantedScopes,
                  })
                ).catch((e) => console.error("❌ Lỗi lưu token:", e));

                // Lấy danh sách fanpage mới
                window.FB.api(
                  "/me/accounts",
                  "get",
                  { access_token: accessToken },
                  async function (apiResponse) {
                    if (apiResponse && !apiResponse.error) {
                      setAvailableFanpages(apiResponse.data);
                      const db2 = getDatabase();
                      const uid = localStorage.getItem("userId");
                      const connectedFanpagesRef = ref(db2, `users/${uid}/connectedFanpages`);
                      try {
                        const grantedScopes = await fetchGrantedFacebookScopes(
                          accessToken
                        );
                        const processedFanpages = [];
                        const updatedFanpages = {};
                        for (const page of apiResponse.data) {
                          try {
                            if (!page.access_token) {
                              const fanpageData = buildConnectedFanpageRecord(page, {
                                accessToken: null,
                                grantedScopes,
                                tokenStatus: "error",
                                tokenError: "Không có access_token từ Facebook API",
                              });
                              updatedFanpages[page.id] = fanpageData;
                              processedFanpages.push(fanpageData);
                              continue;
                            }
                            const exchangeResult = await exchangeForLongLivedToken(page.access_token);
                            let fanpageData = buildConnectedFanpageRecord(page, {
                              accessToken: page.access_token || null,
                              grantedScopes,
                            });
                            if (exchangeResult.success) {
                              fanpageData = {
                                ...fanpageData,
                                access_token: exchangeResult.accessToken || null,
                                tokenStatus: "active",
                                tokenExpiry: null,
                              };
                            } else {
                              fanpageData = {
                                ...fanpageData,
                                access_token: page.access_token || null,
                                tokenStatus: "active",
                                tokenExpiry: null,
                                tokenError:
                                  exchangeResult.error ||
                                  "Không thể chuyển đổi sang long-lived token",
                              };
                            }
                            updatedFanpages[page.id] = fanpageData;
                            processedFanpages.push(fanpageData);
                          } catch (error) {
                            const fanpageData = buildConnectedFanpageRecord(page, {
                              accessToken: page.access_token || null,
                              grantedScopes,
                              tokenStatus: "error",
                              tokenError: error.message || "Unknown error",
                            });
                            updatedFanpages[page.id] = fanpageData;
                            processedFanpages.push(fanpageData);
                          }
                        }
                        const sanitizedUpdatedFanpages = sanitizeForFirebase(updatedFanpages);
                        await set(connectedFanpagesRef, sanitizedUpdatedFanpages);
                        await set(
                          ref(db2, `users/${uid}/connectedChannels/facebook`),
                          buildFacebookConnectedChannelMap(sanitizedUpdatedFanpages)
                        );
                        setConnectedFanpages(processedFanpages);
                        setNeedsFacebookConnect(false);
                        toast.success("Kết nối fanpage thành công!", { position: "top-right", autoClose: 3000 });
                      } catch (error) {
                        toast.error("Không thể lưu thông tin fanpage: " + error.message, { position: "top-right", autoClose: 5000 });
                      } finally {
                        setIsAddingFanpage(false);
                      }
                    } else {
                      toast.error("Không thể lấy thông tin fanpage!", { position: "top-right", autoClose: 5000 });
                      setIsAddingFanpage(false);
                    }
                  }
                );
              } else {
                setIsAddingFanpage(false);
                toast.error("Kết nối Facebook không thành công!", { position: "top-right", autoClose: 5000 });
              }
            })
            .catch((error) => {
              setIsAddingFanpage(false);
              toast.error("Lỗi khi trao đổi token: " + error.message, { position: "top-right", autoClose: 5000 });
            });
        },
        // onFail
        () => {
          setIsAddingFanpage(false);
          toast.error("Kết nối Facebook không thành công!", { position: "top-right", autoClose: 5000 });
        },
        {
          fallbackPendingAction: "connect_fanpage",
        }
      );
    } catch (error) {
      console.error("Error connecting Facebook pages:", error);
      setIsAddingFanpage(false);
      toast.error("Đã xảy ra lỗi khi kết nối với Facebook: " + error.message, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const formatExpirationDate = (timestamp) => {
    if (!timestamp) return "Không có";

    let date;
    if (typeof timestamp === "number") {
      if (timestamp > 100000000000) {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp * 1000);
      }
    } else if (typeof timestamp === "string") {
      date = new Date(timestamp);
    } else if (timestamp instanceof Object && timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      return "Không xác định";
    }

    if (isNaN(date.getTime())) {
      return "Không xác định";
    }

    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getRemainingDays = (timestamp) => {
    if (!timestamp) return null;

    let date;
    if (typeof timestamp === "number") {
      if (timestamp > 100000000000) {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp * 1000);
      }
    } else if (typeof timestamp === "string") {
      date = new Date(timestamp);
    } else if (timestamp instanceof Object && timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      return null;
    }

    if (isNaN(date.getTime())) {
      return null;
    }

    const now = new Date();
    const diff = date.getTime() - now.getTime();

    if (diff < 0) {
      return "Đã hết hạn";
    }

    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const getMembershipName = (membershipId) => {
    if (!membershipId && membershipId !== 0) return "Chưa đăng ký";

    // Normalize to number — admin PATCH may store as string "3" vs payment stores as number 3
    const id = typeof membershipId === "string" ? parseInt(membershipId, 10) : membershipId;

    // Đồng bộ với các gói trong PricingScreen.js
    if (id === 6) return "Trải nghiệm";
    if (id === 1) return "Cơ bản";
    if (id === 2) return "Chuyên nghiệp";
    if (id === 3) return "Cao Cấp";
    if (id === 4) return "VIP";

    return "Không xác định";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-4 md:px-8 pt-6 pb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center mx-auto">
          Thiết Lập Thông Tin
        </h1>
        <ToastContainer position="top-right" autoClose={5000} />

        {/* Tab Navigation */}
        <nav className="profile-tab-nav">
          <button
            className={`profile-tab-btn ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            <span className="tab-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>
            <span className="tab-label">Tổng quan</span>
          </button>
          <button
            className={`profile-tab-btn ${activeTab === "facebook" ? "active" : ""}`}
            onClick={() => setActiveTab("facebook")}
          >
            <span className="tab-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg></span>
            <span className="tab-label">Facebook</span>
          </button>
          <button
            className={`profile-tab-btn ${activeTab === "zalo" ? "active" : ""}`}
            onClick={() => setActiveTab("zalo")}
          >
            <span className="tab-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
            <span className="tab-label">Zalo</span>
          </button>
          <button
            className={`profile-tab-btn ${activeTab === "wordpress" ? "active" : ""}`}
            onClick={() => setActiveTab("wordpress")}
          >
            <span className="tab-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></span>
            <span className="tab-label">WordPress</span>
          </button>
          <button
            className={`profile-tab-btn ${activeTab === "security" ? "active" : ""}`}
            onClick={() => setActiveTab("security")}
          >
            <span className="tab-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></span>
            <span className="tab-label">Bảo mật</span>
          </button>
        </nav>

        {/* ===== TAB: Tổng quan ===== */}
        {activeTab === "overview" && (
        <div className="profile-tab-content">

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            Thông Tin Điểm Sử Dụng
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-gray-700 mb-3">
                Điểm từ gói dịch vụ
              </h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">
                {pointsDetail.points}
              </p>
              <p className="text-sm text-gray-500">
                Điểm từ gói dịch vụ sẽ hết hạn cùng với gói dịch vụ của bạn.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-gray-700 mb-3">
                Điểm mua thêm
              </h3>
              <p className="text-3xl font-bold text-green-600 mb-2">
                {pointsDetail.purchasedPoints}
              </p>
              <p className="text-sm text-gray-500">
                Điểm mua thêm không hết hạn, nhưng cần có gói dịch vụ để sử
                dụng.
              </p>
            </div>
          </div>
          <hr className="my-4" />
          <div className="mt-4 bg-blue-50 p-4 rounded-md">
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              Thông tin gói dịch vụ
            </h3>
            <p className="mb-1">
              <span className="font-medium">Gói hiện tại:</span>{" "}
              <span className="text-blue-600 font-medium">
                {getMembershipName(membershipInfo.currentMembership)}
              </span>
              {isTrial && membershipInfo.currentMembership === 6 && (
                <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs">
                  Gói thử nghiệm
                </span>
              )}
              {membershipInfo.isYearly && (
                <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs">
                  Gói hàng năm
                </span>
              )}
            </p>
            <p className="mb-1">
              <span className="font-medium">Ngày hết hạn:</span>{" "}
              <span className="text-blue-600">
                {formatExpirationDate(membershipInfo.expiryDate)}
              </span>
            </p>
            {getRemainingDays(membershipInfo.expiryDate) && (
              <p className="text-sm text-gray-600">
                {getRemainingDays(membershipInfo.expiryDate) ===
                "Đã hết hạn" ? (
                  <span className="text-red-600 font-medium">Đã hết hạn</span>
                ) : (
                  <span>
                    Còn{" "}
                    <span className="font-medium text-blue-600">
                      {getRemainingDays(membershipInfo.expiryDate)}
                    </span>{" "}
                    ngày
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            Thông Tin Cá Nhân
          </h2>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">
              ID người dùng
            </label>
            <div className="relative">
              <input
                className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600 pr-12"
                value={userId}
                disabled
                type="text"
              />
              <button
                onClick={handleCopyUserId}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Sao chép
              </button>
            </div>
          </div>
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Email
            </label>
            <input
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Nhập địa chỉ email của bạn"
              type="email"
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Số điện thoại
            </label>
            <input
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Nhập số điện thoại của bạn"
              type="tel"
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Số điện thoại giới thiệu
            </label>
            <input
              name="referralPhone"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              value={referralPhone}
              onChange={(e) => setReferralPhone(e.target.value)}
              placeholder="Nhập số điện thoại người giới thiệu (nếu có)"
              type="tel"
            />
            {referralPhone && (
              <p className="mt-2 text-sm text-gray-500">
                Số điện thoại giới thiệu không thể thay đổi sau khi đã nhập
              </p>
            )}
          </div>
        </div>

        {/* Nút Lưu thông tin - trong tab Tổng quan */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <button
            className="flex items-center justify-center w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={handleSave}
          >
            Lưu thông tin
          </button>
        </div>

        </div>
        )}

        {/* ===== TAB: Facebook ===== */}
        {activeTab === "facebook" && (
        <div id="facebook-fanpage-management" className="profile-tab-content">

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">

          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              Quản Lý Fanpage Facebook
            </h2>

            {/* Simplified action buttons - Reduced cognitive load */}
            <div className="flex items-center space-x-2">
              {needsFacebookConnect ? (
                // Primary action when not connected
                <button
                  onClick={handleFacebookConnect}
                  disabled={isLoadingFanpages}
                  className={`px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium ${
                    isLoadingFanpages ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isLoadingFanpages ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Đang kết nối...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4 mr-2 inline-block"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                      Kết nối Facebook
                    </>
                  )}
                </button>
              ) : (
                // Actions when connected - Grouped and prioritized
                <>
                  {/* Primary action */}
                  <button
                    onClick={handleConnectNewPages}
                    disabled={isAddingFanpage || isRefreshingFanpages}
                    className={`px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      isAddingFanpage || isRefreshingFanpages
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {isAddingFanpage ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Đang xử lý...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4 mr-2 inline-block"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Thêm Fanpage
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          {needsFacebookConnect ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-6 w-6 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-medium text-blue-900 mb-2">
                    Kết nối với Facebook
                  </h3>
                  <p className="text-blue-800 mb-4">
                    Để sử dụng tính năng quản lý Fanpage, bạn cần kết nối tài
                    khoản Facebook với các quyền cần thiết.
                  </p>
                  <div className="bg-white rounded-md p-3 mb-4">
                    <p className="text-sm font-medium text-gray-900 mb-2">
                      ✓ Quyền được cấp:
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Xem danh sách fanpage</li>
                      <li>• Đăng bài và quản lý nội dung</li>
                      <li>• Xem thống kê tương tác</li>
                      <li>• Quản lý fanpage doanh nghiệp</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Success state - Connected */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-gray-600">
                    Quản lý các fanpage Facebook đã kết nối để sử dụng trong ứng
                    dụng.
                  </p>
                  {connectedFanpages.length > 0 && (
                    <div className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
                      ✓ {connectedFanpages.length} fanpage đã kết nối
                    </div>
                  )}
                </div>

                {connectedFanpages.length > 0 && (
                  <div className="mb-4 text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded">
                    Cập nhật lần cuối:{" "}
                    {new Date(
                      Math.max(
                        ...connectedFanpages.map(
                          (page) => page.lastRefreshed || 0
                        )
                      )
                    ).toLocaleString("vi-VN")}
                  </div>
                )}
              </div>

              {/* Loading state */}
              {(isLoadingFanpages || isRefreshingFanpages) &&
                connectedFanpages.length === 0 && (
                  <div className="text-center p-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <div className="flex justify-center items-center mb-4">
                      <svg
                        className="animate-spin h-8 w-8 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Đang tải danh sách fanpage...
                    </h3>
                    <p className="text-gray-500">Vui lòng đợi trong giây lát</p>
                  </div>
                )}

              {/* Fanpage list component - Chỉ hiển thị khi có fanpage */}
              {connectedFanpages.length > 0 && (
                <FanpageTokenManager
                  connectedFanpages={connectedFanpages}
                  refreshTrigger={refreshTrigger}
                />
              )}

              {/* Empty state - Chỉ hiển thị khi không có fanpage và không đang loading */}
              {connectedFanpages.length === 0 &&
                !isLoadingFanpages &&
                !isRefreshingFanpages &&
                !needsFacebookConnect && (
                  <div className="text-center p-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Chưa có fanpage nào
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Thêm fanpage Facebook để bắt đầu đăng bài tự động
                    </p>
                    <button
                      onClick={handleConnectNewPages}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Thêm Fanpage Đầu Tiên
                    </button>
                  </div>
                )}
            </>
          )}

          {/* Simplified help text - Only show when relevant */}
          {(needsFacebookConnect ||
            (connectedFanpages.length === 0 &&
              !isLoadingFanpages &&
              !isRefreshingFanpages)) && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-gray-500 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Yêu cầu kết nối
                  </h4>
                  <div className="text-sm text-gray-700 space-y-2">
                    <p>
                      • Bạn phải là <strong>Admin của fanpage</strong> để kết
                      nối
                    </p>
                    <p>
                      • Fanpage doanh nghiệp cần quyền{" "}
                      <strong>Business Manager</strong>
                    </p>
                    <p>
                      • Dữ liệu được bảo mật và không chia sẻ với bên thứ ba
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        </div>
        )}

        {/* ===== TAB: Zalo ===== */}
        {activeTab === "zalo" && (
        <div id="zalo-account-management" className="profile-tab-content">

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              Quản Lý Zalo OA
            </h2>
            <button
              onClick={handleConnectZalo}
              disabled={isConnectingZalo}
              className={`px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isConnectingZalo ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isConnectingZalo ? "Đang kết nối..." : "Kết nối Zalo OA"}
            </button>
          </div>

          {isLoadingZaloAccounts ? (
            <div className="text-gray-500">Đang tải danh sách Zalo OA...</div>
          ) : zaloAccounts.length > 0 ? (
            <div className="space-y-3">
              {zaloAccounts.map((account) => (
                <div
                  key={account.accountId}
                  className="flex items-center justify-between border border-gray-200 rounded-md p-4"
                >
                  <div className="flex items-center gap-3">
                    {account.avatar ? (
                      <img
                        src={account.avatar}
                        alt={account.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold">
                        Z
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-gray-900">
                        {account.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        OA ID: {account.accountId} · Token:{" "}
                        {account.tokenStatus || "active"}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDisconnectZalo(account.accountId)}
                    className="px-3 py-1.5 border border-red-200 text-red-600 rounded-md hover:bg-red-50"
                  >
                    Ngắt kết nối
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Chưa có Zalo OA nào
              </h3>
              <p className="text-gray-500 mb-4">
                Kết nối Zalo OA để đăng bài Article từ các luồng fanpage hiện có.
              </p>
              <button
                onClick={handleConnectZalo}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Kết nối Zalo OA
              </button>
            </div>
          )}
        </div>

        </div>
        )}

        {/* ===== TAB: WordPress ===== */}
        {activeTab === "wordpress" && (
        <div className="profile-tab-content">

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Thiết Lập WordPress Sites
            </h2>
            <button
              onClick={handleAddNewSite}
              disabled={isEditingSite}
              className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isEditingSite ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              + Thêm Website
            </button>
          </div>

          {/* Video hướng dẫn */}
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={handleWordPressGuide}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-medium rounded-lg hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transform transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
              title="Xem video hướng dẫn tạo tài khoản để đăng bài WordPress"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              <span>Tạo tài khoản đăng bài</span>
            </button>
            <button
              onClick={handleMetaDescriptionGuide}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-purple-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transform transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
              title="Xem video hướng dẫn thiết lập thẻ Meta Description cho bài viết"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              <span>Thiết lập Meta Description</span>
            </button>
          </div>

          {/* Hướng dẫn nhanh */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-semibold text-blue-900 flex items-center">
                <svg
                  className="h-5 w-5 text-blue-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Hướng dẫn nhanh
              </h4>
              <button
                onClick={() => setShowDetailedGuide(!showDetailedGuide)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none"
              >
                {showDetailedGuide ? "▲ Ẩn chi tiết" : "▼ Xem chi tiết"}
              </button>
            </div>

            {/* Hướng dẫn ngắn gọn */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-white p-3 rounded-lg border border-blue-100">
                <div className="flex items-center mb-2">
                  <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-full mr-2">
                    1
                  </span>
                  <h5 className="font-medium text-gray-900">Tên người dùng</h5>
                </div>
                <p className="text-sm text-gray-600">
                  WordPress Admin → Người dùng → cột{" "}
                  <strong>"Tên người dùng"</strong>
                </p>
                <p className="text-xs text-green-600 mt-1">
                  VD: "admin", "autowork", "user123"
                </p>
              </div>

              <div className="bg-white p-3 rounded-lg border border-blue-100">
                <div className="flex items-center mb-2">
                  <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-full mr-2">
                    2
                  </span>
                  <h5 className="font-medium text-gray-900">
                    Application Password
                  </h5>
                </div>
                <p className="text-sm text-gray-600">
                  Hồ sơ → Mật khẩu ứng dụng → Tạo mới
                </p>
                <p className="text-xs text-green-600 mt-1">
                  VD: "abcd efgh ijkl mnop"
                </p>
              </div>
            </div>

            {/* Lưu ý quan trọng */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
              <div className="flex items-center">
                <svg
                  className="h-4 w-4 text-amber-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <p className="text-sm font-medium text-amber-800">
                  <strong>Lưu ý:</strong> Dùng "Tên người dùng", không phải "Tên
                  mật khẩu ứng dụng"
                </p>
              </div>
            </div>

            {/* Chi tiết đầy đủ */}
            {showDetailedGuide && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full mr-2">
                        ✓
                      </span>
                      Chi tiết: Tìm Tên người dùng
                    </h5>
                    <ol className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>1. Đăng nhập WordPress Admin</li>
                      <li>
                        2. Vào <strong>Thành viên → Tất cả người dùng</strong>
                      </li>
                      <li>
                        3. Tìm tài khoản của bạn trong cột{" "}
                        <strong>"Tên người dùng"</strong>
                      </li>
                      <li>4. Copy chính xác Tên người dùng đó vào form</li>
                    </ol>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full mr-2">
                        ✓
                      </span>
                      Chi tiết: Tạo Application Password
                    </h5>
                    <ol className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>
                        1. WordPress Admin →{" "}
                        <strong>Thành viên → Chọn 1 thành viên</strong>
                      </li>
                      <li>
                        2. Cuộn xuống phần <strong>"Mật khẩu ứng dụng"</strong>
                      </li>
                      <li>
                        3. Nhập tên: "AutoWork App" ở Tên mật khẩu ứng dụng mới
                      </li>
                      <li>
                        4. Nhấn <strong>"Thêm mật khẩu ứng dụng"</strong>
                      </li>
                      <li>
                        5. Copy mật khẩu có dạng:{" "}
                        <code className="bg-gray-100 px-1 rounded text-xs">
                          abcd efgh ijkl mnop
                        </code>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            )}
          </div>

          {isEditingSite ? (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <svg
                    className="h-5 w-5 text-blue-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  {currentSite.id ? "Chỉnh sửa Website" : "Thêm Website Mới"}
                </h3>
                <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-full">
                  {currentSite.id ? "Cập nhật" : "Mới"}
                </span>
              </div>
              {/* Thông tin cơ bản */}
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tên website <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="siteName"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={currentSite.siteName}
                      onChange={handleSiteInputChange}
                      placeholder="VD: Trang tin tức công ty"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL Website <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="url"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={currentSite.url}
                      onChange={handleSiteInputChange}
                      placeholder="https://example.com"
                      type="url"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                    <span>Thông tin thêm về website</span>
                    <SettingTooltip
                      label="Thông tin thêm về website"
                      text={WEBSITE_INFO_TOOLTIP_TEXT}
                    />
                  </label>
                  <textarea
                    name="websiteInfo"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    value={currentSite.websiteInfo}
                    onChange={handleSiteInputChange}
                    placeholder="Mô tả ngắn gọn về website..."
                    rows={4}
                  />
                </div>

                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                    <span>Thông tin liên hệ</span>
                    <SettingTooltip
                      label="Thông tin liên hệ"
                      text={WEBSITE_CONTACT_INFO_FIELD_TOOLTIP_TEXT}
                    />
                  </label>
                  <textarea
                    name="contactInfo"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    value={currentSite.contactInfo}
                    onChange={handleSiteInputChange}
                    placeholder="Nhập số điện thoại, link Zalo, địa chỉ, hotline...&#10;Ví dụ:&#10;📞 Hotline: 0909 xxx xxx&#10;💬 Zalo: https://zalo.me/...&#10;🏠 Địa chỉ: 123 Nguyễn Văn A, Q1, TP.HCM"
                    rows={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Nội dung này sẽ được tự động chèn vào cuối bài viết khi bật tuỳ chọn "Thêm thông tin liên hệ" lúc tạo bài.
                  </p>
                </div>

                {/* Thông tin đăng nhập */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                    <svg
                      className="h-4 w-4 text-gray-600 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m0 0v4m0-4h4m-4 0H8"
                      />
                    </svg>
                    Thông tin đăng nhập WordPress
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tên người dùng <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="username"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        value={currentSite.username}
                        onChange={handleSiteInputChange}
                        placeholder="VD: admin, autowork, user123..."
                      />
                      <p className="mt-1 text-xs text-amber-600 flex items-start">
                        <svg
                          className="h-3 w-3 text-amber-500 mr-1 mt-0.5 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                        Dùng Tên người dùng, không phải Tên mật khẩu ứng dụng
                        mới
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Application Password{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          name="password"
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={currentSite.password}
                          onChange={handleSiteInputChange}
                          placeholder="abcd efgh ijkl mnop"
                          type={showPassword ? "text" : "password"}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? (
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Định dạng:{" "}
                        <code className="bg-gray-100 px-1 rounded text-xs">
                          abcd efgh ijkl mnop
                        </code>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Hiển thị trạng thái kết nối */}
                {connectionStatus && (
                  <div
                    className={`mt-4 p-3 rounded-md border ${
                      connectionStatus.success
                        ? "bg-green-50 border-green-200"
                        : "bg-red-50 border-red-200"
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        {connectionStatus.success ? (
                          <svg
                            className="h-5 w-5 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="h-5 w-5 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="ml-3 flex-1">
                        <p
                          className={`text-sm font-medium ${
                            connectionStatus.success
                              ? "text-green-800"
                              : "text-red-800"
                          }`}
                        >
                          {connectionStatus.success
                            ? "Kết nối thành công!"
                            : "Lỗi kết nối"}
                        </p>
                        <p
                          className={`text-sm mt-1 ${
                            connectionStatus.success
                              ? "text-green-700"
                              : "text-red-700"
                          }`}
                        >
                          {connectionStatus.message}
                        </p>
                        {connectionStatus.success && connectionStatus.user && (
                          <div className="mt-2 text-xs text-green-600">
                            <p>
                              Tài khoản: {connectionStatus.user.name} (ID:{" "}
                              {connectionStatus.user.id})
                            </p>
                            {connectionStatus.user.roles &&
                              connectionStatus.user.roles.length > 0 && (
                                <p>
                                  Quyền:{" "}
                                  {connectionStatus.user.roles.join(", ")}
                                </p>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
                  {/* Nút kiểm tra kết nối */}
                  <button
                    onClick={async () => {
                      if (
                        !currentSite.url ||
                        !currentSite.username ||
                        !currentSite.password
                      ) {
                        toast.error(
                          "Vui lòng nhập đầy đủ URL, tên người dùng và mật khẩu trước khi kiểm tra kết nối!",
                          {
                            position: "top-right",
                            autoClose: 4000,
                          }
                        );
                        return;
                      }

                      setIsTestingConnection(true);
                      setConnectionStatus(null);

                      try {
                        const result = await testWordPressConnection(
                          currentSite
                        );
                        setConnectionStatus({
                          success: result.success,
                          message: result.message,
                          user: result.user,
                        });

                        if (result.success) {
                          toast.success(result.message, {
                            position: "top-right",
                            autoClose: 3000,
                          });
                        } else {
                          toast.error(result.message, {
                            position: "top-right",
                            autoClose: 5000,
                          });
                        }
                      } catch (error) {
                        setConnectionStatus({
                          success: false,
                          message: "Lỗi khi kiểm tra kết nối",
                        });
                        toast.error(
                          "Lỗi khi kiểm tra kết nối: " + error.message,
                          {
                            position: "top-right",
                            autoClose: 5000,
                          }
                        );
                      } finally {
                        setIsTestingConnection(false);
                      }
                    }}
                    disabled={isTestingConnection}
                    className={`flex-1 sm:flex-none sm:px-6 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      isTestingConnection
                        ? "bg-blue-400 text-white cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500"
                    }`}
                  >
                    {isTestingConnection ? (
                      <div className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Đang kiểm tra...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Kiểm tra kết nối WordPress
                      </div>
                    )}
                  </button>

                  {/* Nút lưu và hủy */}
                  <div className="flex space-x-4">
                    <button
                      onClick={handleSaveSite}
                      disabled={isTestingConnection}
                      className={`px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        isTestingConnection
                          ? "bg-green-400 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                      }`}
                    >
                      {isTestingConnection ? (
                        <div className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Đang xử lý...
                        </div>
                      ) : (
                        "Lưu Website"
                      )}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isTestingConnection}
                      className={`px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        isTestingConnection
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-gray-500 hover:bg-gray-600 focus:ring-gray-500"
                      }`}
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {wordpressSites.length > 0 ? (
            <div className="space-y-4">
              {wordpressSites.map((site) => (
                <div
                  key={site.id}
                  className="border border-gray-200 rounded-md p-4 hover:bg-gray-50"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium text-gray-800 text-lg">
                        {site.siteName}
                      </h3>
                      <p className="text-gray-600">{site.url}</p>
                      <p className="text-gray-500 text-sm mb-2">
                        Tên người dùng:{" "}
                        <span className="font-mono bg-gray-100 px-1 rounded">
                          {site.username}
                        </span>
                      </p>
                      
                      {/* Trạng thái SEO Connector */}
                      <div className="flex items-center mt-2">
                        {site.seoConnectorInstalled ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <svg className="-ml-0.5 mr-1.5 h-3 w-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            SEO Connector v{site.seoConnectorVersion || "1.0"}
                          </span>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <svg className="-ml-0.5 mr-1.5 h-3 w-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              Thiếu SEO Connector
                            </span>
                            <button
                              onClick={() => {
                                setSelectedSiteForConnector(site);
                                setShowConnectorDialog(true);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            >
                              Cài đặt ngay
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditSite(site)}
                        disabled={isEditingSite}
                        className={`p-2 text-blue-600 hover:bg-blue-100 rounded ${
                          isEditingSite ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteSite(site.id)}
                        disabled={isEditingSite}
                        className={`p-2 text-red-600 hover:bg-red-100 rounded ${
                          isEditingSite ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-8 bg-gray-50 rounded-md">
              <p className="text-gray-500">
                Chưa có website nào được thiết lập
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Nhấn nút "Thêm Website" để bắt đầu
              </p>
            </div>
          )}
        </div>

        </div>
        )}

        {/* ===== TAB: Bảo mật & Hỗ trợ ===== */}
        {activeTab === "security" && (
        <div className="profile-tab-content">

        {/* Bảo mật tài khoản */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            Bảo Mật Tài Khoản
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Trạng thái đăng nhập:{" "}
            {hasPassword ? (
              <span className="text-green-600 font-medium">
                Đã thiết lập mật khẩu
              </span>
            ) : (
              <span className="text-orange-600 font-medium">
                Chưa thiết lập mật khẩu
              </span>
            )}
          </p>

          {!hasPassword && (
            <div className="space-y-3">
              <input
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="password"
                placeholder="Nhập mật khẩu mới"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <input
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="password"
                placeholder="Xác nhận mật khẩu mới"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={handleSetupPassword}
                disabled={isSettingPassword}
                className={`px-4 py-2 rounded-md text-white ${
                  isSettingPassword
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isSettingPassword
                  ? "Đang thiết lập..."
                  : "Thiết lập mật khẩu đăng nhập"}
              </button>
            </div>
          )}

          {hasPassword && (
            <div className="space-y-3">
              <input
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="password"
                placeholder="Mật khẩu hiện tại"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <input
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="password"
                placeholder="Mật khẩu mới"
                value={changePasswordValue}
                onChange={(e) => setChangePasswordValue(e.target.value)}
              />
              <input
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="password"
                placeholder="Xác nhận mật khẩu mới"
                value={confirmChangePassword}
                onChange={(e) => setConfirmChangePassword(e.target.value)}
              />
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                className={`px-4 py-2 rounded-md text-white ${
                  isChangingPassword
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isChangingPassword ? "Đang đổi mật khẩu..." : "Đổi mật khẩu"}
              </button>
            </div>
          )}
        </div>

        {/* Hỗ Trợ Kỹ Thuật */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Hỗ Trợ Kỹ Thuật
          </h2>
          <p className="text-gray-600 mb-6">
            Gặp vấn đề khi thiết lập? Liên hệ với chúng tôi qua Zalo hoặc
            Facebook để được hỗ trợ nhanh chóng!
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Zalo Support Button */}
            <a
              href="https://zalo.me/0394932696"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <span className="font-medium">Hỗ trợ qua Zalo</span>
            </a>

            {/* Facebook Support Button */}
            <a
              href="https://www.facebook.com/people/AutoWork/61564488969068/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 transform transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <span className="font-medium">Hỗ trợ qua Facebook</span>
            </a>
          </div>
        </div>

        </div>
        )}

      </div>
      
      {/* Dialog Hướng dẫn cài đặt SEO Connector */}
      {showConnectorDialog && selectedSiteForConnector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">
                Cài đặt AutoWork SEO Connector
              </h3>
              <button
                onClick={() => setShowConnectorDialog(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      Plugin này giúp AutoWork tự động cập nhật thẻ Meta Title, Meta Description, Focus Keyword (Yoast SEO/Rank Math) và Alt Text hình ảnh cho website <strong>{selectedSiteForConnector.siteName}</strong>.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold">1</div>
                  <div className="ml-4 flex-1">
                    <h4 className="text-lg font-medium text-gray-900">Tải file plugin</h4>
                    <p className="mt-1 text-gray-600 mb-3">Tải file định dạng ZIP về máy tính của bạn.</p>
                    <a
                      href={getConnectorDownloadUrl()}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="mr-2 -ml-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Tải file autowork-seo-connector.zip
                    </a>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold">2</div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-gray-900">Upload lên WordPress</h4>
                    <p className="mt-1 text-gray-600">Đăng nhập vào trang quản trị WordPress của bạn, vào menu <strong>Plugins &rarr; Add New</strong>, nhấn nút <strong>Upload Plugin</strong> ở trên cùng.</p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold">3</div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-gray-900">Cài đặt và Kích hoạt</h4>
                    <p className="mt-1 text-gray-600">Chọn file .zip vừa tải về, nhấn <strong>Install Now</strong>, sau đó nhấn <strong>Activate Plugin</strong>.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowConnectorDialog(false)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium transition-colors"
              >
                Để sau
              </button>
              <button
                onClick={() => handleCheckConnector(selectedSiteForConnector)}
                disabled={checkingConnector}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                  checkingConnector ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {checkingConnector ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Đang kiểm tra...
                  </>
                ) : (
                  "Tôi đã Activate - Kiểm tra lại"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;
